import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@icons/ExpandMoreIcon";
import DataHistogramIcon from "@icons/DataHistogramIcon";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  facilityComplianceService,
  FacilityComplianceData,
  FacilityCompliancePayload,
  getAccreditationProgramStandardCode,
  getAccreditationProgramSubject,
  getPendingItemDisplayText,
} from "@services/facilityCompliance";
import {
  formatDate,
  formatCurrencyAmount,
  formatCurrency,
  formatLicenseFee,
} from "@utils/formatUtils";
import { differenceInDays, startOfDay } from "date-fns";
import { getCurrentUserId, isGlobalAdministrator } from "@utils/userUtils";
import {
  getQuartersFromDate,
  isDateInQuarter,
  isOngoingCalendarItem,
} from "@components/Spaces/Penalty";
import { isTaxComplianceCalendarItem } from "@utils/penaltyCalendarTax";
import { stripMarkdownLight } from "@utils/stripMarkdownLight";
import {
  SELECTED_FACILITY_STORAGE_KEY,
  SELECTED_FACILITY_UPDATED_EVENT,
  mergeFacilityDetailsForTaxonomy,
} from "@utils/facilityUtils";
import AccreditationCalendarView, { AccreditationSectionsView } from "@components/Spaces/AccreditationCalendarView";
import FacilityTypeValueStack from "@components/Spaces/FacilityTypeValueStack";
import { AuditChecklistTable } from "@components/Spaces/AuditChecklistTable";
import { CompliancePenaltyTooltipTitle } from "@components/Spaces/CompliancePenaltyTooltipTitle";

const MainContainer = styled(Box)({
  display: "flex",
  width: "100%",
  height: "99vh",
  background: "#ECFCFF",
});
const ContentWrapper = styled(Box)({
  padding: "8px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "48px",
  overflowY: "auto",
});
const MainContent = styled(Box)({
  width: "100%",
  maxWidth: "1000px",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "center",
});
const HeaderSection = styled(Box)({
  padding: "16px",
  width: "100%",
  height: "529px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});
const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  height: "99vh",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  boxSizing: "border-box",
});

const HeaderContainer = styled(Box)({
  padding: "8px 0 0 0",
  borderBottom: "1px solid #35d7e4",
  width: "100%",
});

const Breadcrumbs = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginTop: "5px",
  marginBottom: "16px",
  fontSize: "12px",
  color: "#262626",
  //   fontFamily: "Inter, sans-serif",
});
const BreadcrumbLink = styled(Typography)({
  fontSize: "13px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px", // 133.333%
  letterSpacing: "0.03px",
  cursor: "pointer",
  "&:hover": {
    color: "#086e86",
  },
  "&.active": {
    color: "#086e86",
    fontWeight: 500,
  },
});
const ComplianceMainHeader = styled(Box)({
  alignItems: "center",
  gap: "16px",
  alignSelf: "stretch",
  paddingBottom: "2px",
  mb: "14px",

  display: "flex",
  padding: "8px 0",
  // flexDirection: "column",
});
const ComplianceHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px",
  background: "#fff",
  alignSelf: "stretch",
});

const StyledAccordion = styled(Accordion)({
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "#262626",
});

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M15 12.5854C15.5826 12.7913 16 13.3469 16 14V14.5C16 15.6046 15.1046 16.5 14 16.5H6C4.89543 16.5 4 15.6046 4 14.5V9C4 8.34689 4.4174 7.79127 5 7.58535V6C5 5.17157 5.67157 4.5 6.5 4.5H11.5C12.3284 4.5 13 5.17157 13 6V12.5H14L13.9997 6.58525C14.5825 6.79109 15 7.34678 15 8V12.5854ZM12 11.7929V6C12 5.72386 11.7761 5.5 11.5 5.5H6.5C6.22386 5.5 6 5.72386 6 6V7.5H7.08579C7.48361 7.5 7.86514 7.65804 8.14645 7.93934L12 11.7929ZM5 9V14.5C5 15.0523 5.44772 15.5 6 15.5H14C14.5523 15.5 15 15.0523 15 14.5V14C15 13.7239 14.7761 13.5 14.5 13.5H12.9142C12.5164 13.5 12.1349 13.342 11.8536 13.0607L7.43934 8.64645C7.34557 8.55268 7.21839 8.5 7.08579 8.5H5.5C5.22386 8.5 5 8.72386 5 9Z"
      fill="#065B70"
    />
  </svg>
);
const LicencingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M16.5625 2.50077L16.5 2.5H6.5C5.11929 2.5 4 3.61929 4 5V13.5H2C1.44772 13.5 1 13.9477 1 14.5V16.1875C1 17.5211 2.0858 18.5 3.375 18.5H12.5156C13.8787 18.4917 14.9831 17.3924 14.9998 16.0313L15 16V7.5H18C18.5523 7.5 19 7.05229 19 6.5V5C19 3.64018 17.9143 2.53394 16.5625 2.50077ZM6.5 3.5H14.4998C14.2093 3.88671 14.0284 4.3604 14.0031 4.875H14V16C14 16.8284 13.3284 17.5 12.5 17.5C11.6716 17.5 11 16.8284 11 16V14.5C11 13.9477 10.5523 13.5 10 13.5H5V5C5 4.17157 5.67157 3.5 6.5 3.5ZM16.4988 3.5H16.5012C17.3291 3.50065 18 4.17198 18 5V6.5H15V5C15 4.17198 15.6709 3.50065 16.4988 3.5ZM2 14.5H10V16C10 16.5628 10.186 17.0822 10.4998 17.5H3.375C2.59313 17.5 2 16.9249 2 16.1875V14.5Z"
      fill="#262626"
    />
  </svg>
);
const AccreditationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M4 3.5C2.89543 3.5 2 4.39543 2 5.5V9.17133C2.28512 8.81899 2.62236 8.51056 3 8.25777V5.5C3 4.94772 3.44772 4.5 4 4.5H16C16.5523 4.5 17 4.94772 17 5.5V13.5C17 14.0523 16.5523 14.5 16 14.5H9.24223C9.16639 14.6133 9.08554 14.723 9 14.8287V15.5H16C17.1046 15.5 18 14.6046 18 13.5V5.5C18 4.39543 17.1046 3.5 16 3.5H4ZM5 7C5 6.72386 5.22386 6.5 5.5 6.5H14.5C14.7761 6.5 15 6.72386 15 7C15 7.27614 14.7761 7.5 14.5 7.5H5.5C5.22386 7.5 5 7.27614 5 7ZM5.5 15.5C3.567 15.5 2 13.933 2 12C2 10.067 3.567 8.5 5.5 8.5C7.433 8.5 9 10.067 9 12C9 13.933 7.433 15.5 5.5 15.5ZM3 15.7422C3.71505 16.2209 4.57493 16.5 5.5 16.5C6.42507 16.5 7.28495 16.2209 8 15.7422V18.5C8 18.912 7.52962 19.1472 7.2 18.9L5.8 17.85C5.62222 17.7167 5.37778 17.7167 5.2 17.85L3.8 18.9C3.47038 19.1472 3 18.912 3 18.5V15.7422ZM10.5 10.5C10.2239 10.5 10 10.7239 10 11C10 11.2761 10.2239 11.5 10.5 11.5H14.5C14.7761 11.5 15 11.2761 15 11C15 10.7239 14.7761 10.5 14.5 10.5H10.5Z"
      fill="#262626"
    />
  </svg>
);
const ComplianceIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M3 3.75C3 3.61193 3.11193 3.5 3.25 3.5H8.75C8.88807 3.5 9 3.61193 9 3.75V6.25C9 6.94036 9.55964 7.5 10.25 7.5H11.75C11.8881 7.5 12 7.61193 12 7.75V8.5H13V7.75C13 7.05964 12.4404 6.5 11.75 6.5H10.25C10.1119 6.5 10 6.38807 10 6.25V3.75C10 3.05964 9.44036 2.5 8.75 2.5H3.25C2.55964 2.5 2 3.05964 2 3.75V14.25C2 14.9404 2.55964 15.5 3.25 15.5H7V14.5H3.25C3.11193 14.5 3 14.3881 3 14.25V3.75ZM7.25 9C7.52312 9 7.76213 9.14598 7.89328 9.36418C7.57415 9.679 7.32522 10.0648 7.17212 10.496C6.7945 10.457 6.5 10.1379 6.5 9.75C6.5 9.33579 6.83579 9 7.25 9ZM4.75 6C5.16421 6 5.5 5.66421 5.5 5.25C5.5 4.83579 5.16421 4.5 4.75 4.5C4.33579 4.5 4 4.83579 4 5.25C4 5.66421 4.33579 6 4.75 6ZM5.5 7.5C5.5 7.91421 5.16421 8.25 4.75 8.25C4.33579 8.25 4 7.91421 4 7.5C4 7.08579 4.33579 6.75 4.75 6.75C5.16421 6.75 5.5 7.08579 5.5 7.5ZM4.75 10.5C5.16421 10.5 5.5 10.1642 5.5 9.75C5.5 9.33579 5.16421 9 4.75 9C4.33579 9 4 9.33579 4 9.75C4 10.1642 4.33579 10.5 4.75 10.5ZM8 5.25C8 5.66421 7.66421 6 7.25 6C6.83579 6 6.5 5.66421 6.5 5.25C6.5 4.83579 6.83579 4.5 7.25 4.5C7.66421 4.5 8 4.83579 8 5.25ZM7.25 8.25C7.66421 8.25 8 7.91421 8 7.5C8 7.08579 7.66421 6.75 7.25 6.75C6.83579 6.75 6.5 7.08579 6.5 7.5C6.5 7.91421 6.83579 8.25 7.25 8.25ZM8 11.5C8 10.3954 8.89543 9.5 10 9.5H17C18.1046 9.5 19 10.3954 19 11.5V15.5C19 16.6046 18.1046 17.5 17 17.5H15V18.5H16.5C16.7761 18.5 17 18.7239 17 19C17 19.2761 16.7761 19.5 16.5 19.5H10.5C10.2239 19.5 10 19.2761 10 19C10 18.7239 10.2239 18.5 10.5 18.5H12V17.5H10C8.89543 17.5 8 16.6046 8 15.5V11.5ZM10 16.5H17C17.5523 16.5 18 16.0523 18 15.5V11.5C18 10.9477 17.5523 10.5 17 10.5H10C9.44772 10.5 9 10.9477 9 11.5V15.5C9 16.0523 9.44772 16.5 10 16.5ZM14 18.5V17.5H13V18.5H14Z"
      fill="#262626"
    />
  </svg>
);
const RequirementsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M3 3.75C3 3.61193 3.11193 3.5 3.25 3.5H8.75C8.88807 3.5 9 3.61193 9 3.75V6.25C9 6.94036 9.55964 7.5 10.25 7.5H11.75C11.8881 7.5 12 7.61193 12 7.75V8.5H13V7.75C13 7.05964 12.4404 6.5 11.75 6.5H10.25C10.1119 6.5 10 6.38807 10 6.25V3.75C10 3.05964 9.44036 2.5 8.75 2.5H3.25C2.55964 2.5 2 3.05964 2 3.75V14.25C2 14.9404 2.55964 15.5 3.25 15.5H7V14.5H3.25C3.11193 14.5 3 14.3881 3 14.25V3.75ZM7.25 9C7.52312 9 7.76213 9.14598 7.89328 9.36418C7.57415 9.679 7.32522 10.0648 7.17212 10.496C6.7945 10.457 6.5 10.1379 6.5 9.75C6.5 9.33579 6.83579 9 7.25 9ZM4.75 6C5.16421 6 5.5 5.66421 5.5 5.25C5.5 4.83579 5.16421 4.5 4.75 4.5C4.33579 4.5 4 4.83579 4 5.25C4 5.66421 4.33579 6 4.75 6ZM5.5 7.5C5.5 7.91421 5.16421 8.25 4.75 8.25C4.33579 8.25 4 7.91421 4 7.5C4 7.08579 4.33579 6.75 4.75 6.75C5.16421 6.75 5.5 7.08579 5.5 7.5ZM4.75 10.5C5.16421 10.5 5.5 10.1642 5.5 9.75C5.5 9.33579 5.16421 9 4.75 9C4.33579 9 4 9.33579 4 9.75C4 10.1642 4.33579 10.5 4.75 10.5ZM8 5.25C8 5.66421 7.66421 6 7.25 6C6.83579 6 6.5 5.66421 6.5 5.25C6.5 4.83579 6.83579 4.5 7.25 4.5C7.66421 4.5 8 4.83579 8 5.25ZM7.25 8.25C7.66421 8.25 8 7.91421 8 7.5C8 7.08579 7.66421 6.75 7.25 6.75C6.83579 6.75 6.5 7.08579 6.5 7.5C6.5 7.91421 6.83579 8.25 7.25 8.25ZM8 11.5C8 10.3954 8.89543 9.5 10 9.5H17C18.1046 9.5 19 10.3954 19 11.5V15.5C19 16.6046 18.1046 17.5 17 17.5H15V18.5H16.5C16.7761 18.5 17 18.7239 17 19C17 19.2761 16.7761 19.5 16.5 19.5H10.5C10.2239 19.5 10 19.2761 10 19C10 18.7239 10.2239 18.5 10.5 18.5H12V17.5H10C8.89543 17.5 8 16.6046 8 15.5V11.5ZM10 16.5H17C17.5523 16.5 18 16.0523 18 15.5V11.5C18 10.9477 17.5523 10.5 17 10.5H10C9.44772 10.5 9 10.9477 9 11.5V15.5C9 16.0523 9.44772 16.5 10 16.5ZM14 18.5V17.5H13V18.5H14Z"
      fill="#262626"
    />
  </svg>
);
const CheckListIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M5.85355 4.85355C6.04882 4.65829 6.04882 4.34171 5.85355 4.14645C5.65829 3.95118 5.34171 3.95118 5.14645 4.14645L3.5 5.79289L2.85355 5.14645C2.65829 4.95118 2.34171 4.95118 2.14645 5.14645C1.95118 5.34171 1.95118 5.65829 2.14645 5.85355L3.14645 6.85355C3.34171 7.04882 3.65829 7.04882 3.85355 6.85355L5.85355 4.85355ZM8.5 5.5C8.22386 5.5 8 5.72386 8 6C8 6.27614 8.22386 6.5 8.5 6.5H17.5C17.7761 6.5 18 6.27614 18 6C18 5.72386 17.7761 5.5 17.5 5.5H8.5ZM8.5 10.5C8.22386 10.5 8 10.7239 8 11C8 11.2761 8.22386 11.5 8.5 11.5H17.5C17.7761 11.5 18 11.2761 18 11C18 10.7239 17.7761 10.5 17.5 10.5H8.5ZM8 16C8 15.7239 8.22386 15.5 8.5 15.5H17.5C17.7761 15.5 18 15.7239 18 16C18 16.2761 17.7761 16.5 17.5 16.5H8.5C8.22386 16.5 8 16.2761 8 16ZM5.85355 10.3536C6.04882 10.1583 6.04882 9.84171 5.85355 9.64645C5.65829 9.45118 5.34171 9.45118 5.14645 9.64645L3.5 11.2929L2.85355 10.6464C2.65829 10.4512 2.34171 10.4512 2.14645 10.6464C1.95118 10.8417 1.95118 11.1583 2.14645 11.3536L3.14645 12.3536C3.34171 12.5488 3.65829 12.5488 3.85355 12.3536L5.85355 10.3536ZM5.85355 14.6464C6.04882 14.8417 6.04882 15.1583 5.85355 15.3536L3.85355 17.3536C3.65829 17.5488 3.34171 17.5488 3.14645 17.3536L2.14645 16.3536C1.95118 16.1583 1.95118 15.8417 2.14645 15.6464C2.34171 15.4512 2.65829 15.4512 2.85355 15.6464L3.5 16.2929L5.14645 14.6464C5.34171 14.4512 5.65829 14.4512 5.85355 14.6464Z"
      fill="#262626"
    />
  </svg>
);
const PenaltyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M7.56268 1.76181C7.75183 1.41271 8.25284 1.41273 8.44195 1.76186L14.9425 13.7628C15.1229 14.0959 14.8817 14.5009 14.5028 14.5009H1.50015C1.12124 14.5009 0.880028 14.0959 1.06053 13.7627L7.56268 1.76181ZM9.32124 1.28557C8.75391 0.238205 7.25088 0.238125 6.68344 1.28544L0.18129 13.2864C-0.360219 14.2858 0.363428 15.5009 1.50015 15.5009H14.5028C15.6395 15.5009 16.3631 14.286 15.8218 13.2865L9.32124 1.28557ZM8.50069 6.00023C8.50069 5.72409 8.27683 5.50023 8.00069 5.50023C7.72455 5.50023 7.50069 5.72409 7.50069 6.00023V10.0002C7.50069 10.2764 7.72455 10.5002 8.00069 10.5002C8.27683 10.5002 8.50069 10.2764 8.50069 10.0002V6.00023ZM8.75069 12.2502C8.75069 12.6644 8.41491 13.0002 8.00069 13.0002C7.58648 13.0002 7.25069 12.6644 7.25069 12.2502C7.25069 11.836 7.58648 11.5002 8.00069 11.5002C8.41491 11.5002 8.75069 11.836 8.75069 12.2502Z"
      fill="#262626"
    />
  </svg>
);
const CfrRegulationsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M4.5 2.5C3.39543 2.5 2.5 3.39543 2.5 4.5V16.5C2.5 17.6046 3.39543 18.5 4.5 18.5H15.5C16.6046 18.5 17.5 17.6046 17.5 16.5V7.5L12.5 2.5H4.5ZM4.5 3.5H12V7.5C12 8.05228 12.4477 8.5 13 8.5H16.5V16.5C16.5 17.0523 16.0523 17.5 15.5 17.5H4.5C3.94772 17.5 3.5 17.0523 3.5 16.5V4.5C3.5 3.94772 3.94772 3.5 4.5 3.5ZM13 3.70711L16.2929 7H13V3.70711ZM6 10.5C5.72386 10.5 5.5 10.7239 5.5 11C5.5 11.2761 5.72386 11.5 6 11.5H14C14.2761 11.5 14.5 11.2761 14.5 11C14.5 10.7239 14.2761 10.5 14 10.5H6ZM6 13C5.72386 13 5.5 13.2239 5.5 13.5C5.5 13.7761 5.72386 14 6 14H14C14.2761 14 14.5 13.7761 14.5 13.5C14.5 13.2239 14.2761 13 14 13H6ZM6 15.5C5.72386 15.5 5.5 15.7239 5.5 16C5.5 16.2761 5.72386 16.5 6 16.5H11C11.2761 16.5 11.5 16.2761 11.5 16C11.5 15.7239 11.2761 15.5 11 15.5H6Z"
      fill="#065B70"
    />
  </svg>
);

// Interface is now imported from service

// ---------------------------------------------------------------------------
// Penalties tab-table helpers (mirrors Penalty.tsx)
// ---------------------------------------------------------------------------

const fcPenaltyTableHeaderSx = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#FFF",
  fontFamily: "Rethink Sans, sans-serif",
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: "1px solid #0a6a80",
  padding: "10px 12px",
  backgroundColor: "#0a829d",
};

const fcPenaltyTableCellSx = {
  fontSize: "13px",
  fontFamily: "Rethink Sans, sans-serif",
  color: "#262626",
  borderBottom: "1px solid #f0f0f0",
  padding: "10px 12px",
  verticalAlign: "top" as const,
};

function fcDetectRecurring(item: any): boolean {
  const notes = (item?.workedExample?.calculationNotes ?? "").toLowerCase();
  const formula = (item?.formula ?? "").toLowerCase();
  const rule = (item?.ruleOrQuote ?? "").toLowerCase();
  const combined = `${notes} ${formula} ${rule}`;
  if (/\bnot recurring\b|one.time|single.time/.test(combined)) return false;
  return /\brecurring\b|per.day|per day|daily|per.violation|accruing|until corrected|continuing/.test(
    combined,
  );
}

function fcExtractContingentRiskInfo(risk: any): {
  name: string;
  trigger: string;
  exposure: string;
  riskLevel: "High" | "Medium" | "Low" | null;
} {
  const text: string = risk?.text ?? "";
  const dashIdx = text.indexOf(" — ");
  const dotIdx = text.indexOf(". ");
  let name = text;
  let trigger = text;
  if (dashIdx > 0) {
    name = text.slice(0, dashIdx).trim();
    trigger = text.slice(dashIdx + 3).trim();
  } else if (dotIdx > 0 && dotIdx < 80) {
    name = text.slice(0, dotIdx).trim();
    trigger = text.trim();
  }

  const expMatch =
    text.match(/up to \$([\d,]+(?:\.\d+)?)/i) ??
    text.match(/\$([\d,]+(?:\.\d+)?)(?:\s*per\s*(\w+))?/i);
  let exposure = "—";
  if (expMatch) {
    const raw = expMatch[1].replace(/,/g, "");
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const perMatch = text.match(/per\s+(\w+)/i);
      const unit = perMatch ? perMatch[1].toLowerCase() : null;
      exposure = `≤ ${formatCurrencyAmount(num)}${unit ? ` /${unit}` : ""}`;
    }
  }

  const lower = text.toLowerCase();
  let riskLevel: "High" | "Medium" | "Low" | null = null;
  if (/\bhigh\b/.test(lower)) riskLevel = "High";
  else if (/\bmedium\b/.test(lower)) riskLevel = "Medium";
  else if (/\blow\b/.test(lower)) riskLevel = "Low";
  else if (/\$10[,.]?000,000|\$10m|\$10 million/.test(lower)) riskLevel = "High";
  else if (/per day|daily|accruing/.test(lower)) riskLevel = "Medium";

  return { name, trigger, exposure, riskLevel };
}

function getAviationProfilePayloadFields(facility: any): Partial<FacilityCompliancePayload> {
  const profileTargeting =
    facility?.profile_targeting_json || facility?.aviation_targeting || null;
  return {
    vertical: facility?.vertical ?? undefined,
    primary_profile_slug: facility?.primary_profile_slug ?? undefined,
    profile_targeting_json: profileTargeting,
    activity_domains:
      facility?.activity_domains ?? (profileTargeting?.activity_domains as string[] | undefined),
    operation_contexts:
      facility?.operation_contexts ?? (profileTargeting?.operation_contexts as string[] | undefined),
    asset_types:
      facility?.asset_types ?? (profileTargeting?.asset_types as string[] | undefined),
    certificates_or_approvals:
      facility?.certificates_or_approvals ??
      (profileTargeting?.certificates_or_approvals as string[] | undefined),
    regulation_packs:
      facility?.regulation_packs ?? (profileTargeting?.regulation_packs as string[] | undefined),
    overlay_packs:
      facility?.overlay_packs ?? (profileTargeting?.overlay_packs as string[] | undefined),
    penalty_packs:
      facility?.penalty_packs ?? (profileTargeting?.penalty_packs as string[] | undefined),
  };
}

const FacilityCompliance: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [complianceData, setComplianceData] =
    useState<FacilityComplianceData | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [expandedCfr, setExpandedCfr] = useState<string | false>(false);
  const [selectedFacilityPrefsEpoch, setSelectedFacilityPrefsEpoch] = useState(0);

  useEffect(() => {
    const onPrefs = () => setSelectedFacilityPrefsEpoch((n) => n + 1);
    window.addEventListener(SELECTED_FACILITY_UPDATED_EVENT, onPrefs);
    return () => window.removeEventListener(SELECTED_FACILITY_UPDATED_EVENT, onPrefs);
  }, []);
  const [penaltySubTab, setPenaltySubTab] = useState<
    "penalties" | "recurring" | "contingent"
  >("penalties");

  const fcRecurringCostsTabCount = useMemo(
    () =>
      complianceData?.sections?.penaltiesAndTotalExposure?.rollup?.recurringAnnualCosts?.filter(
        (c) => c != null,
      ).length ?? 0,
    [
      complianceData?.sections?.penaltiesAndTotalExposure?.rollup
        ?.recurringAnnualCosts,
    ],
  );

  useEffect(() => {
    if (penaltySubTab === "recurring" && fcRecurringCostsTabCount === 0) {
      setPenaltySubTab("penalties");
    }
  }, [penaltySubTab, fcRecurringCostsTabCount]);

  // Get facility data from navigation state
  const facilityData = location.state?.facilityData;
  
  // Helper function to get facility data with fallback to sessionStorage
  const getFacilityData = () => {
    if (facilityData) {
      return facilityData;
    }
    // Fallback to sessionStorage
    try {
      const stored = sessionStorage.getItem("filtered_selectedFacility");
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error("Error parsing facility data from sessionStorage:", err);
      return null;
    }
  };
  
  console.log("facilityData", facilityData);
  useEffect(() => {
    const fetchComplianceData = async () => {
      try {
        // Get facility data with fallback
        const currentFacilityData = getFacilityData();
        
        if (currentFacilityData) {
          // Extract state and city with proper string conversion and trimming
          const state = currentFacilityData.state ? String(currentFacilityData.state).trim() : "";
          const city = currentFacilityData.city ? String(currentFacilityData.city).trim() : "";
          
          console.log("🏥 Facility Compliance - Facility Data:", {
            fromProp: !!facilityData,
            fromSessionStorage: !facilityData,
            facilityData: currentFacilityData,
            state: state || "(empty)",
            city: city || "(empty)",
          });
          
          const userId = parseInt(getCurrentUserId(), 10) || 0;
          const aviationProfilePayload =
            getAviationProfilePayloadFields(currentFacilityData);
          const payload: FacilityCompliancePayload = {
            user_id: userId,
            organization:
              currentFacilityData.site_name || "EvergreenHealth Medical Center",
            facility_name:
              currentFacilityData.site_name || "EvergreenHealth Medical Center",
            facility_type:
              aviationProfilePayload.primary_profile_slug ||
              currentFacilityData.facility_type_slug ||
              currentFacilityData.facility_type,
            country: getSelectedCountryCode(),
            state: state,
            county: "",
            city: city,
            site_url: "",
            documents: "",
            as_of_date: new Date().toISOString().split('T')[0],
            response_language: "English",
            instructions:
              "Search government regulatory sites for compliance requirements, permits, licenses, and medical facility documentation for EvergreenHealth Medical Center",
            output_format: "JSON",
            org_name: "test",
            facility_type_id:
              currentFacilityData?.vertical === "aviation"
                ? undefined
                : (currentFacilityData as { facility_type_id?: number }).facility_type_id,
            ...aviationProfilePayload,
          };

          // For now, use mock data. In production, replace with:
          // const data = await facilityComplianceService.getFacilityCompliance(payload);
          const data = await facilityComplianceService.getFacilityCompliance(
            payload
          );
          setComplianceData(data);
        } else {
          
        }
      } catch (error) {
        console.error("Error fetching compliance data:", error);
        
      }
    };

    fetchComplianceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityData, getSelectedCountryCode()]);

  // Also watch for country changes via storage and refresh
  useEffect(() => {
    const checkCountryChange = () => {
      setComplianceData(null); // clear to show loaders if any
      // trigger re-fetch by forcing effect dependency change via location/state
      // simplest: call fetch directly with latest facilityData
      // but we can't access inner function here; instead, navigate state doesn't change.
      // So we re-run the same logic here inline
      (async () => {
        try {
          // Get facility data with fallback
          const currentFacilityData = getFacilityData();
          
          if (currentFacilityData) {
            // Extract state and city with proper string conversion and trimming
            const state = currentFacilityData.state ? String(currentFacilityData.state).trim() : "";
            const city = currentFacilityData.city ? String(currentFacilityData.city).trim() : "";
            const userId = parseInt(getCurrentUserId(), 10) || 0;
            const aviationProfilePayload =
              getAviationProfilePayloadFields(currentFacilityData);
            const payload: FacilityCompliancePayload = {
              user_id: userId,
              organization: currentFacilityData.site_name || "EvergreenHealth Medical Center",
              facility_name: currentFacilityData.site_name || "EvergreenHealth Medical Center",
              facility_type:
                aviationProfilePayload.primary_profile_slug ||
                currentFacilityData.facility_type_slug ||
                currentFacilityData.facility_type,
              country: getSelectedCountryCode(),
              state: state,
              county: "",
              city: city,
              site_url: "",
              documents: "",
              as_of_date: new Date().toISOString().split('T')[0],
              response_language: "English",
              instructions:
                "Search government regulatory sites for compliance requirements, permits, licenses, and medical facility documentation for EvergreenHealth Medical Center",
              output_format: "JSON",
              org_name: "test",
              facility_type_id:
                currentFacilityData?.vertical === "aviation"
                  ? undefined
                  : (currentFacilityData as { facility_type_id?: number }).facility_type_id,
              ...aviationProfilePayload,
            };
            const data = await facilityComplianceService.getFacilityCompliance(payload);
            setComplianceData(data);
          }
        } catch (e) {
          console.error(e);
        }
      })();
    };

    const interval = setInterval(checkCountryChange, 500);
    window.addEventListener("storage", checkCountryChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkCountryChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityData]);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleCfrAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCfr(isExpanded ? panel : false);
    };

  const handleEditClick = () => {
    const currentFacilityData = getFacilityData();
    const preselectedVertical =
      currentFacilityData?.vertical ||
      currentFacilityData?.facility_details?.vertical ||
      currentFacilityData?.compliance_type ||
      "healthcare";
    navigate("/dashboard/MyFacility", {
      state: { currentView: "facilityValidation", preselectedVertical },
    });
  };
  const handleClick = () => {
    sessionStorage.setItem("DisplayPage", "true");
    navigate("/dashboard/facilities", {
      state: { currentView: "MyFacility" },
    });
  };
  const stored = sessionStorage.getItem("filtered_selectedFacility");
  let filtered_selectedFacility: {
    site_name?: string;
    city?: string;
    state?: string;
    [key: string]: any;
  } | null = null;

  try {
    filtered_selectedFacility = stored ? JSON.parse(stored) : null;
  } catch (err) {
    console.error("Invalid JSON in sessionStorage:", stored, err);
  }

  const facilityTypeDisplayMerged = mergeFacilityDetailsForTaxonomy({
    ...(filtered_selectedFacility || {}) as Record<string, unknown>,
    facility_type:
      complianceData?.entityDetails?.facility_type ??
      filtered_selectedFacility?.facility_type,
  });

  const penaltiesComplianceCalendar =
    complianceData?.sections?.penaltiesAndTotalExposure?.complianceCalendar;
  const complianceCalendarSource = useMemo(
    () => penaltiesComplianceCalendar ?? [],
    [penaltiesComplianceCalendar],
  );
  const entityAsOf = complianceData?.entityDetails?.asOfDate;
  const effectiveAsOfForComplianceCalendar =
    entityAsOf && /^\d{4}-\d{2}-\d{2}/.test(entityAsOf) ? entityAsOf : new Date().toISOString().split("T")[0];
  const complianceCalendarQuarterCount = isGlobalAdministrator() ? 4 : 2;
  const complianceCalendarQuarterConfig = useMemo(
    () => getQuartersFromDate(effectiveAsOfForComplianceCalendar, complianceCalendarQuarterCount),
    [effectiveAsOfForComplianceCalendar, complianceCalendarQuarterCount],
  );
  const showTaxComplianceCalendar = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(SELECTED_FACILITY_STORAGE_KEY);
      if (!raw) return false;
      const p = JSON.parse(raw) as { show_tax_compliance_calendar?: boolean };
      return p.show_tax_compliance_calendar === true;
    } catch {
      return false;
    }
  }, [facilityData, complianceData, selectedFacilityPrefsEpoch]);

  useEffect(() => {
    if (!showTaxComplianceCalendar && expanded === "taxComplianceCalendar") {
      setExpanded(false);
    }
  }, [expanded, showTaxComplianceCalendar]);

  type FacilityComplianceCalendarAccordionVm = {
    accordionKey: "complianceCalendar" | "taxComplianceCalendar";
    title: string;
    items: typeof complianceCalendarSource;
  };

  // Same split as Potential Penalties (Penalty.tsx): TITLE26 rows never in facility calendar table.
  const facilityComplianceCalendarAccordions = useMemo((): FacilityComplianceCalendarAccordionVm[] => {
    const applyQuarterVisibility = (
      items: typeof complianceCalendarSource,
    ): typeof complianceCalendarSource => {
      if (isGlobalAdministrator()) return items;
      return items.filter(
        (item) =>
          isOngoingCalendarItem(item) ||
          complianceCalendarQuarterConfig.some((q) =>
            isDateInQuarter(item.deadline, q, item),
          ),
      );
    };
    const facilityItems = complianceCalendarSource.filter(
      (i) => !isTaxComplianceCalendarItem(i),
    );
    const taxItems = complianceCalendarSource.filter((i) =>
      isTaxComplianceCalendarItem(i),
    );
    const visibleFacility = applyQuarterVisibility(facilityItems);
    const visibleTax = applyQuarterVisibility(taxItems);
    const out: FacilityComplianceCalendarAccordionVm[] = [];
    if (visibleFacility.length > 0) {
      out.push({
        accordionKey: "complianceCalendar",
        title: "Facility Compliance Calendar",
        items: visibleFacility,
      });
    }
    if (showTaxComplianceCalendar) {
      out.push({
        accordionKey: "taxComplianceCalendar",
        title: "Tax Compliance Calendar",
        items: visibleTax,
      });
    }
    return out;
  }, [
    complianceCalendarSource,
    complianceCalendarQuarterConfig,
    showTaxComplianceCalendar,
  ]);

  if (!complianceData) {
    return (
      <MainContainer>
        <ContentContainer>
          <ContentWrapper>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                minHeight: "40vh",
              }}
            >
              <CircularProgress size={48} sx={{ color: "#0a829d" }} />
              <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 500, textAlign: "center" }}>
                Analyzing facility compliance data…
              </Typography>
              <Typography sx={{ color: "#737373", fontSize: 14, fontWeight: 400, textAlign: "center" }}>
                This AI-assisted analysis may take a few minutes.
              </Typography>
            </Box>
          </ContentWrapper>
        </ContentContainer>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ContentContainer>
        <ContentWrapper>
          <MainContent>
            <HeaderSection>
              {/* Header with Back Button */}
              <HeaderContainer>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    border: "1px solid #CCF5FF",
                    borderRadius: "12px",
                    mb: 2,
                  }}
                >
                  {/* Facility Info */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      gap: 3,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#065B70",
                      }}
                    >
                      Selected Facility
                    </Typography>

                    {/* Custom "box" styled like a textfield */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #99D4E1",
                        borderRadius: "8px",
                        background: "#FDFFFF",
                        px: 2,
                        py: 1,
                        gap: 2,
                        width: "430px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          color: "#262626",
                        }}
                      >
                        {filtered_selectedFacility?.site_name || ""} –{" "}
                        {filtered_selectedFacility?.city || ""},{" "}
                        {filtered_selectedFacility?.state || ""}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#737373",
                        whiteSpace: "nowrap",
                        ml: "10px",
                        mr: "10px",
                        width: "270px",
                      }}
                    >
                      {filtered_selectedFacility?.city},{" "}
                      {filtered_selectedFacility?.state}, USA
                    </Typography>

                    <IconButton
                      size="small"
                      sx={{ alignItems: "flex-end" }}
                      onClick={handleEditClick}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                      >
                        <path
                          d="M23.1813 8.92689C22.0291 7.71505 20.1047 7.69077 18.9222 8.87317L9.54741 18.2475C9.21958 18.5754 8.99204 18.9899 8.89148 19.4424L8.01387 23.3923C7.97678 23.5592 8.02754 23.7335 8.14844 23.8544C8.26934 23.9753 8.44362 24.026 8.6105 23.9889L12.5369 23.1157C13.0043 23.0118 13.4324 22.7767 13.771 22.4381L23.129 13.08C24.27 11.939 24.2933 10.0963 23.1813 8.92689ZM19.6293 9.58029C20.4143 8.79538 21.6917 8.8115 22.4566 9.61596C23.1948 10.3923 23.1793 11.6155 22.4219 12.3729L21.7507 13.0442L18.958 10.2516L19.6293 9.58029ZM18.2509 10.9586L21.0436 13.7513L13.0639 21.731C12.8598 21.9352 12.6016 22.0769 12.3198 22.1396L9.1605 22.8421L9.86768 19.6593C9.92698 19.3924 10.0612 19.148 10.2545 18.9547L18.2509 10.9586Z"
                          fill="#065B70"
                        />
                      </svg>
                    </IconButton>
                  </Box>
                </Box>

                <Breadcrumbs>
                  <BreadcrumbLink onClick={handleEditClick}>
                    My Facility
                  </BreadcrumbLink>
                  <span> › </span>
                  <BreadcrumbLink onClick={handleClick}>
                    {filtered_selectedFacility?.category}
                  </BreadcrumbLink>
                  <span> › </span>
                  <BreadcrumbLink
                    className="active"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <FacilityTypeValueStack
                      facility={facilityTypeDisplayMerged}
                      mergeDetails={false}
                      sxPrimary={{
                        fontSize: "13px",
                        fontWeight: 400,
                        lineHeight: "16px",
                        letterSpacing: "0.03px",
                        color: "inherit",
                      }}
                      sxSecondary={{
                        fontSize: "11px",
                        fontWeight: 400,
                        color: "#5D6B72",
                        lineHeight: "14px",
                      }}
                    />
                  </BreadcrumbLink>
                </Breadcrumbs>

                <ComplianceMainHeader sx={{ alignItems: "flex-start" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ flexShrink: 0, marginTop: 4 }}
                  >
                    <path
                      d="M3 6.25C3 4.45507 4.45507 3 6.25 3H17.75C19.5449 3 21 4.45507 21 6.25V17.75C21 19.5449 19.5449 21 17.75 21H6.25C4.45507 21 3 19.5449 3 17.75V6.25ZM13.4905 9.49994L8.22232 14.7171C7.928 15.0086 7.92569 15.4834 8.21715 15.7777C8.50862 16.0721 8.98349 16.0744 9.2778 15.7829L14.5 10.6113V15.2499C14.5 15.6642 14.8358 15.9999 15.25 15.9999C15.6642 15.9999 16 15.6642 16 15.2499V8.74994C16 8.33573 15.6642 7.99994 15.25 7.99994H8.75C8.33579 7.99994 8 8.33573 8 8.74994C8 9.16415 8.33579 9.49994 8.75 9.49994H13.4905Z"
                      fill="#05495A"
                    />
                  </svg>
                  <FacilityTypeValueStack
                    facility={facilityTypeDisplayMerged}
                    mergeDetails={false}
                    sxPrimary={{
                      color: "var(--Primary-Color-Palette-800, #05495A)",
                      fontFamily: "Rethink Sans",
                      fontSize: "24px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "150%",
                      letterSpacing: "-0.4px",
                    }}
                    sxSecondary={{
                      fontSize: "14px",
                      fontWeight: 400,
                      color: "#5D6B72",
                      marginTop: "2px",
                    }}
                  />
                </ComplianceMainHeader>
              </HeaderContainer>
              {/* Compliance Analysis Header */}
              <ComplianceHeader>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="21"
                  viewBox="0 0 20 21"
                  fill="none"
                >
                  <path
                    d="M8.5 6.5V15.5H11.5V6.5C11.5 5.94772 11.0523 5.5 10.5 5.5H9.5C8.94772 5.5 8.5 5.94772 8.5 6.5ZM7.5 9.5V6.5C7.5 5.39543 8.39543 4.5 9.5 4.5H10.5C11.6046 4.5 12.5 5.39543 12.5 6.5V7.5H14C15.1046 7.5 16 8.39543 16 9.5V16C16 16.2761 15.7761 16.5 15.5 16.5H4.5C4.22386 16.5 4 16.2761 4 16V11.5C4 10.3954 4.89543 9.5 6 9.5H7.5ZM7.5 15.5V10.5H6C5.44772 10.5 5 10.9477 5 11.5V15.5H7.5ZM12.5 15.5H15V9.5C15 8.94772 14.5523 8.5 14 8.5H12.5V15.5Z"
                    fill="#065B70"
                  />
                </svg>
                <Typography
                  sx={{
                    color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: 500,
                    lineHeight: "normal",
                  }}
                >
                  Compliance Analysis Complete
                </Typography>
              </ComplianceHeader>
              {/* Accordion Sections */}
              <Box sx={{ maxWidth: "920px", mb: "2px" }}>
                {/* Certificate of Need Summary - Only show if section exists in API response */}
                {complianceData?.sections?.certificateOfNeedSummary && (
                <StyledAccordion
                  expanded={expanded === "certificate"}
                  onChange={handleAccordionChange("certificate")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <MenuIcon />
                      <AccordionTitle>
                        Provide a health department summary, Certificate of
                        Need, or other.
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      // border: "2px solid #35d7e4",
                      // borderRadius: "4px",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {(() => {
                        const con = complianceData?.sections?.certificateOfNeedSummary;
                        const showProjectDescription =
                          con?.projectDescription != null &&
                          String(con.projectDescription).trim() !== "";
                        const showConditionsOfApproval =
                          Array.isArray(con?.conditionsOfApproval) &&
                          con.conditionsOfApproval.length > 0;
                        const acc = con?.approvedCapitalCost;
                        const amount = acc?.amount ?? null;
                        const hasAmount =
                          amount != null && Number(amount) !== 0;
                        const notes = acc?.notes != null ? String(acc.notes).trim() : "";
                        const showApprovedCapitalCost =
                          hasAmount || (notes !== "" && notes !== "N/A");
                        const t = con?.timeline;
                        const showTimeline =
                          t &&
                          [t.applicationDate, t.reviewDate, t.issuanceDate, t.expirationDate].some(
                            (d) => d != null && String(d).trim() !== ""
                          );
                        return (
                          <>
                      {/* Project Description */}
                      {showProjectDescription && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Project Description
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #5bbfd6",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#065b70",
                              lineHeight: 1.4,
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {
                              complianceData.sections.certificateOfNeedSummary
                                .projectDescription
                            }
                          </Typography>
                        </Box>
                      </Box>
                      )}

                      {/* Conditions of Approval */}
                      {showConditionsOfApproval && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Conditions of Approval
                          </Typography>
                        </Box>
                        {complianceData.sections.certificateOfNeedSummary.conditionsOfApproval.map(
                          (condition, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections
                                    .certificateOfNeedSummary
                                    .conditionsOfApproval.length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  borderBottom: "1px solid #3095ab",
                                  paddingBottom: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  Condition {index + 1}
                                </Typography>
                              </Box>
                              <Box sx={{ paddingLeft: "8px" }}>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#065b70",
                                    lineHeight: 1.4,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {condition.text}
                                </Typography>
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                      )}

                      {/* Approved Capital Cost */}
                      {showApprovedCapitalCost && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Approved Capital Cost
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #5bbfd6",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              borderBottom: "1px solid #3095ab",
                              paddingBottom: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: "90px",
                                paddingX: "24px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Capital Expenditure
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                backgroundColor: "#fcfeff",
                                padding: "8px 12px",
                                borderRadius: "4px",
                                border: "1px solid #99d4e1",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                {formatCurrencyAmount(
                                  complianceData.sections
                                    .certificateOfNeedSummary
                                    ?.approvedCapitalCost?.amount ?? 0
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ paddingLeft: "8px" }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: "#065b70",
                                lineHeight: 1.4,
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {complianceData.sections.certificateOfNeedSummary
                                .approvedCapitalCost.notes ||
                                "No additional notes available"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      )}

                      {/* Timeline */}
                      {showTimeline && (
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Timeline
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #5bbfd6",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {[
                            {
                              label: "Application :",
                              value:
                                complianceData.sections.certificateOfNeedSummary
                                  .timeline.applicationDate,
                            },
                            {
                              label: "Review :",
                              value:
                                complianceData.sections.certificateOfNeedSummary
                                  .timeline.reviewDate,
                            },
                            {
                              label: "Issuance :",
                              value:
                                complianceData.sections.certificateOfNeedSummary
                                  .timeline.issuanceDate,
                            },
                            {
                              label: "Expiration :",
                              value:
                                complianceData.sections.certificateOfNeedSummary
                                  .timeline.expirationDate,
                            },
                          ].map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: "128px",
                                  paddingX: "24px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {item.label}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  backgroundColor: "#fcfeff",
                                  padding: "8px 12px",
                                  borderRadius: "4px",
                                  border: "1px solid #99d4e1",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {formatDate(item.value) || "N/A"}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      )}

                          </>
                        );
                      })()}

                      {/* Key Findings */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Key Findings
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {[
                            {
                              title: "Need",
                              content:
                                complianceData.sections.certificateOfNeedSummary
                                  .findings.need.text,
                            },
                            {
                              title: "Financial Feasibility:",
                              content:
                                complianceData.sections.certificateOfNeedSummary
                                  .findings.financialFeasibility.text,
                            },
                            {
                              title: "Structure/Process of Care:",
                              content:
                                complianceData.sections.certificateOfNeedSummary
                                  .findings.structureProcessOfCare.text,
                            },
                            {
                              title: "Cost Containment:",
                              content:
                                complianceData.sections.certificateOfNeedSummary
                                  .findings.costContainment.text,
                            },
                          ].map((finding, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                              }}
                            >
                              <Box
                                sx={{
                                  borderBottom: "1px solid #3095ab",
                                  paddingBottom: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {finding.title}
                                </Typography>
                              </Box>
                              <Box sx={{ paddingLeft: "8px" }}>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#065b70",
                                    lineHeight: 1.4,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {finding.content}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>
                )}

                {/* Licensing Requirements */}
                <StyledAccordion
                  expanded={expanded === "licensing"}
                  onChange={handleAccordionChange("licensing")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LicencingIcon />
                      <AccordionTitle>
                        What are the licensing requirements
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      // border: "2px solid #35d7e4",
                      // borderRadius: "4px",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {/* Country Level */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Country Level
                          </Typography>
                        </Box>
                        {complianceData.sections.licensingRequirements.country.map(
                          (license, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections.licensingRequirements
                                    .country.length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: "200px",
                                    paddingX: "24px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {license.name}
                                  </Typography>
                                </Box>
                                {(() => {
                                  const feeInfo = formatLicenseFee(license?.fee, license);
                                  return (
                                    <>
                                      <Box
                                        sx={{
                                          backgroundColor: feeInfo.isUnknown ? "#fff7ed" : "#fcfeff",
                                          padding: "8px 12px",
                                          borderRadius: "4px",
                                          border: feeInfo.isUnknown ? "1px solid #fed7aa" : "1px solid #99d4e1",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: feeInfo.isUnknown ? "#c2410c" : "#065b70",
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontStyle: feeInfo.isEstimate ? "italic" : "normal",
                                          }}
                                          title={feeInfo.notes || undefined}
                                        >
                                          {feeInfo.displayText}
                                        </Typography>
                                        {feeInfo.workedExampleText && (
                                          <Typography
                                            sx={{
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              color: feeInfo.isUnknown ? "#c2410c" : "#065b70",
                                              fontFamily: "Rethink Sans, sans-serif",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {feeInfo.workedExampleText}
                                          </Typography>
                                        )}
                                      </Box>
                                    </>
                                  );
                                })()}
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>

                      {/* State/Province Level */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            State/Province Level
                          </Typography>
                        </Box>
                        {complianceData.sections.licensingRequirements.state.map(
                          (license, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections.licensingRequirements
                                    .state.length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: "200px",
                                    paddingX: "24px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {license.name}
                                  </Typography>
                                </Box>
                                {(() => {
                                  const feeInfo = formatLicenseFee(license?.fee, license);
                                  return (
                                    <>
                                      <Box
                                        sx={{
                                          backgroundColor: feeInfo.isUnknown ? "#fff7ed" : "#fcfeff",
                                          padding: "8px 12px",
                                          borderRadius: "4px",
                                          border: feeInfo.isUnknown ? "1px solid #fed7aa" : "1px solid #99d4e1",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: feeInfo.isUnknown ? "#c2410c" : "#065b70",
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontStyle: feeInfo.isEstimate ? "italic" : "normal",
                                          }}
                                          title={feeInfo.notes || undefined}
                                        >
                                          {feeInfo.displayText}
                                        </Typography>
                                        {feeInfo.workedExampleText && (
                                          <Typography
                                            sx={{
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              color: feeInfo.isUnknown ? "#c2410c" : "#065b70",
                                              fontFamily: "Rethink Sans, sans-serif",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {feeInfo.workedExampleText}
                                          </Typography>
                                        )}
                                      </Box>
                                    </>
                                  );
                                })()}
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>

                      {/* County Level */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            County Level
                          </Typography>
                        </Box>
                        {complianceData.sections.licensingRequirements.county.map(
                          (license, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections.licensingRequirements
                                    .county.length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: "200px",
                                    paddingX: "24px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {license.name}
                                  </Typography>
                                </Box>
                                {(() => {
                                  const feeInfo = formatLicenseFee(license?.fee, license);
                                  return (
                                    <>
                                      <Box
                                        sx={{
                                          backgroundColor: feeInfo.isUnknown ? "#fff7ed" : "#fcfeff",
                                          padding: "8px 12px",
                                          borderRadius: "4px",
                                          border: feeInfo.isUnknown ? "1px solid #fed7aa" : "1px solid #99d4e1",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: feeInfo.isUnknown ? "#c2410c" : "#065b70",
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontStyle: feeInfo.isEstimate ? "italic" : "normal",
                                          }}
                                          title={feeInfo.notes || undefined}
                                        >
                                          {feeInfo.displayText}
                                        </Typography>
                                        {feeInfo.workedExampleText && (
                                          <Typography
                                            sx={{
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              color: feeInfo.isUnknown ? "#c2410c" : "#065b70",
                                              fontFamily: "Rethink Sans, sans-serif",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {feeInfo.workedExampleText}
                                          </Typography>
                                        )}
                                      </Box>
                                    </>
                                  );
                                })()}
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>

                      {/* City Level */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            City Level
                          </Typography>
                        </Box>
                        {complianceData.sections.licensingRequirements.city.map(
                          (license, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections.licensingRequirements
                                    .city.length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: "200px",
                                    paddingX: "24px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {license.name}
                                  </Typography>
                                </Box>
                                {(() => {
                                  const feeInfo = formatLicenseFee(license?.fee, license);
                                  return (
                                    <>
                                      <Box
                                        sx={{
                                          backgroundColor: "#fcfeff",
                                          padding: "8px 12px",
                                          borderRadius: "4px",
                                          border: "1px solid #99d4e1",
                                          maxWidth: feeInfo.isUnknown ? "300px" : "auto",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: feeInfo.isUnknown ? "#6b7280" : "#065b70",
                                            fontFamily: "Rethink Sans, sans-serif",
                                            fontStyle: feeInfo.isUnknown ? "italic" : "normal",
                                          }}
                                        >
                                          {feeInfo.displayText}
                                        </Typography>
                                        {feeInfo.workedExampleText && (
                                          <Typography
                                            sx={{
                                              fontSize: "12px",
                                              fontWeight: 500,
                                              color: feeInfo.isUnknown ? "#6b7280" : "#065b70",
                                              fontFamily: "Rethink Sans, sans-serif",
                                              marginTop: "4px",
                                            }}
                                          >
                                            {feeInfo.workedExampleText}
                                          </Typography>
                                        )}
                                      </Box>
                                    </>
                                  );
                                })()}
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                {/* Accreditation Requirements */}
                <StyledAccordion
                  expanded={expanded === "accreditation"}
                  onChange={handleAccordionChange("accreditation")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccreditationIcon />
                      <AccordionTitle>
                        What are the accreditation requirements?
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      // border: "2px solid #35d7e4",
                      // borderRadius: "4px",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {/* Conditions of Participation */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Federal certification (CoP/CfC)
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {complianceData.sections.accreditationRequirements.conditionsOfParticipation.map(
                            (condition, index) => (
                              <Box
                                key={index}
                                sx={{
                                  backgroundColor: "#f8fdfe",
                                  padding: "16px 8px",
                                  borderRadius: "8px",
                                  border: "1px solid #5bbfd6",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    lineHeight: 1.4,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {condition}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                      </Box>

                      {/* Accrediting Organizations */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Accrediting Organizations
                          </Typography>
                        </Box>
                        {complianceData.sections.accreditationRequirements.accreditingOrganizations.map(
                          (org, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections
                                    .accreditationRequirements
                                    .accreditingOrganizations.length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  borderBottom: "1px solid #3095ab",
                                  paddingBottom: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {org.citation?.url ? (
                                    <a
                                      href={org.citation.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#2563eb",
                                        textDecoration: "none",
                                      }}
                                    >
                                      {org.name}
                                    </a>
                                  ) : (
                                    org.name
                                  )}
                                </Typography>
                              </Box>
                              <Box sx={{ paddingLeft: "8px" }}>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#065b70",
                                    lineHeight: 1.4,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {org.deemedStatusRequirements}
                                </Typography>
                              </Box>
                              {org.citation?.url && org.citation?.title && (
                                <Box sx={{ paddingLeft: "8px", marginTop: "8px" }}>
                                  <Box
                                    sx={{
                                      backgroundColor: "white",
                                      padding: "4px 16px",
                                      borderRadius: "16px",
                                      border: "1px solid #ccf2f9",
                                      display: "inline-block",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        color: "#065b70",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      <a
                                        href={org.citation.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#6b7280",
                                          textDecoration: "none",
                                        }}
                                      >
                                        📎 {org.citation.title}
                                      </a>
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )
                        )}
                      </Box>

                      {/* Program-Specific Requirements */}
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Program-Specific Requirements
                          </Typography>
                        </Box>
                        {complianceData.sections.accreditationRequirements.programSpecific.map(
                          (program, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                                marginBottom:
                                  index <
                                  complianceData.sections
                                    .accreditationRequirements.programSpecific
                                    .length -
                                    1
                                    ? "8px"
                                    : "0",
                              }}
                            >
                              <Box
                                sx={{
                                  borderBottom: "1px solid #3095ab",
                                  paddingBottom: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#065b70",
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {program.citation?.url ? (
                                    <a
                                      href={program.citation.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: "#2563eb",
                                        textDecoration: "none",
                                      }}
                                    >
                                      {program.program}
                                    </a>
                                  ) : (
                                    program.program
                                  )}
                                </Typography>
                              </Box>
                              <Box
                                sx={{ paddingLeft: "8px", marginBottom: "8px" }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#065b70",
                                    lineHeight: 1.4,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  {program.requirements}
                                </Typography>
                              </Box>
                              <Box sx={{ paddingLeft: "8px" }}>
                                <Box
                                  sx={{
                                    backgroundColor: "white",
                                    padding: "4px 16px",
                                    borderRadius: "16px",
                                    border: "1px solid #ccf2f9",
                                    display: "inline-block",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {program.citation?.url ? (
                                      <a
                                        href={program.citation.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: "#6b7280",
                                          textDecoration: "none",
                                        }}
                                      >
                                        📎 {program.citation.title}
                                      </a>
                                    ) : (
                                      <>Source: {program.citation?.title}</>
                                    )}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                {/* Facility Compliance Requirements */}
                <StyledAccordion
                  expanded={expanded === "facilityCompliance"}
                  onChange={handleAccordionChange("facilityCompliance")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ComplianceIcon />
                      <AccordionTitle>
                        What are facility compliance requirements?
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      // border: "2px solid #35d7e4",
                      // borderRadius: "4px",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "24px",
                      }}
                    >
                      {/* Federal Compliance Requirements Section */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "12px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Federal Compliance Requirements
                          </Typography>
                        </Box>

                        {/* CMS */}
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #cff7ff",
                            marginBottom: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                              marginBottom: "8px",
                            }}
                          >
                            CMS
                          </Typography>

                          {/* Enrollment */}
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Enrollment
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#065b70",
                                  lineHeight: 1.4,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                • Complete <strong>CMS-855A</strong> enrollment
                                for Medicare as an FQHC
                                <br />• Enroll as a Medicaid provider through
                                Washington State Health Care Authority
                              </Typography>
                            </Box>
                          </Box>

                          {/* CoPsOrCfCs */}
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                CoPs Or CfCs
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#065b70",
                                  lineHeight: 1.4,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                • Comply with <strong>42 CFR Part 491</strong> -
                                Certification of Certain Health Centers
                                including sliding fee scale, governance board
                                composition, and scope of services
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* HIPAA */}
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #cff7ff",
                            marginBottom: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                              marginBottom: "8px",
                            }}
                          >
                            HIPAA
                          </Typography>

                          {/* Required Policies */}
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                RequiredPolicies
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#065b70",
                                  lineHeight: 1.4,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                •{" "}
                                <strong>
                                  Privacy Rule compliance policy (45 CFR Part
                                  164 Subpart E)
                                </strong>
                                <br />•{" "}
                                <strong>
                                  Security Rule compliance policy (45 CFR Part
                                  164 Subpart C)
                                </strong>
                              </Typography>
                            </Box>
                          </Box>

                          {/* Risk Analysis Tasks */}
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                RiskAnalysisTasks:
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#065b70",
                                  lineHeight: 1.4,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                • Conduct initial Security Risk Analysis per{" "}
                                <strong>45 CFR 164.308</strong>(a)(1)(ii)(A)
                                <br />• Implement risk management plan to
                                address identified vulnerabilities
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* OSHA */}
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "8px",
                            borderRadius: "8px",
                            border: "1px solid #cff7ff",
                            marginBottom: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                              marginBottom: "8px",
                            }}
                          >
                            OSHA
                          </Typography>

                          {/* Programs */}
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Programs:
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#065b70",
                                  lineHeight: 1.4,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                • Bloodborne Pathogens Standard{" "}
                                <strong>(29 CFR 1910.1030)</strong>
                                <br />• Hazard Communication Standard (
                                <strong>29 CFR 1910.1200</strong>)
                              </Typography>
                            </Box>
                          </Box>

                          {/* Training */}
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                            }}
                          >
                            <Box
                              sx={{
                                borderBottom: "1px solid #3095ab",
                                paddingBottom: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Training:
                              </Typography>
                            </Box>
                            <Box sx={{ paddingLeft: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#065b70",
                                  lineHeight: 1.4,
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                • Annual bloodborne pathogen training for all
                                staff
                                <br />• Hazard communication training for all
                                staff handling chemicals
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Other — all accreditation programs (from rules engine / aggregator); hidden when no data */}
                        {complianceData.sections.otherRequirements &&
                          Object.keys(complianceData.sections.otherRequirements).filter((k) => complianceData.sections.otherRequirements![k]).length > 0 && (
                          <Box
                            sx={{
                              marginTop: "12px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#065b70",
                                fontFamily: "Rethink Sans, sans-serif",
                                marginBottom: "12px",
                              }}
                            >
                              Other
                            </Typography>
                            {Object.entries(complianceData.sections.otherRequirements).map(([programKey, programData]) => {
                              if (!programData) return null;
                              const displayName = programData.program ?? programKey.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
                              const standardCode = getAccreditationProgramStandardCode(programData);
                              const subject = getAccreditationProgramSubject(programData);
                              const titleWithCode = standardCode
                                ? subject
                                  ? `${displayName} — ${standardCode} (${subject})`
                                  : `${displayName} — ${standardCode}`
                                : subject
                                  ? `${displayName} (${subject})`
                                  : displayName;
                              return (
                                <StyledAccordion
                                  key={programKey}
                                  expanded={expanded === programKey}
                                  onChange={handleAccordionChange(programKey)}
                                  sx={{ marginBottom: 1 }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                      borderRadius: "4px",
                                      border: "1px solid #9EE5F4",
                                      backgroundColor: "#FFF",
                                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#065b70",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {titleWithCode}
                                      {programData.revision && (
                                        <Typography
                                          component="span"
                                          sx={{
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            color: "#6b7280",
                                            ml: 1,
                                          }}
                                        >
                                          (Revision {programData.revision}
                                          {programData.effectiveDate
                                            ? `, effective ${programData.effectiveDate}`
                                            : ""}
                                        </Typography>
                                      )}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ pt: 0 }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                      {programData.sections && programData.sections.length > 0 && (
                                        <AccreditationSectionsView
                                          sections={programData.sections}
                                          programLinks={programData.programLinks}
                                        />
                                      )}
                                      {programData.calendarItems && programData.calendarItems.length > 0 && (
                                        <Box sx={{ marginTop: 2 }}>
                                          <AccreditationCalendarView
                                            calendarItems={programData.calendarItems}
                                            revision={programData.revision}
                                            effectiveDate={programData.effectiveDate}
                                            programLinks={programData.programLinks}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  </AccordionDetails>
                                </StyledAccordion>
                              );
                            })}
                          </Box>
                        )}
                      </Box>

                      {/* Implementation Timeline Section */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "12px",
                          }}
                        >
                          <Box sx={{ width: "20px", height: "20px" }}>
                            <DataHistogramIcon size={16} color="#065b70" />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 500,
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Implementation Timeline
                          </Typography>
                        </Box>

                        {[
                          {
                            title: "Planning and Governance",
                            duration: "8 weeks",
                            milestones:
                              "Form governing board, Adopt bylaws, Secure funding commitments",
                          },
                          {
                            title: "Licensing and Enrollment",
                            duration: "16 weeks",
                            milestones:
                              "Submit state facility license application, Submit CMS-855A and Medicaid enrollment, Complete local permits",
                          },
                          {
                            title: "Policy and Program Development",
                            duration: "12 weeks",
                            milestones:
                              "Develop HIPAA policies, Develop emergency preparedness plan, Implement QAPI program",
                          },
                          {
                            title: "Staffing and Training",
                            duration: "10 weeks",
                            milestones:
                              "Hire key clinical and administrative staff, Complete initial HIPAA and OSHA training, Credential all providers",
                          },
                          {
                            title: "Go-Live Preparation",
                            duration: "6 weeks",
                            milestones:
                              "Test EHR system, Conduct mock patient visits, Final readiness review",
                          },
                        ].map((phase, index) => (
                          <Box
                            key={index}
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #cff7ff",
                              marginBottom: index < 4 ? "8px" : "0",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#065b70",
                                fontFamily: "Rethink Sans, sans-serif",
                                marginBottom: "8px",
                              }}
                            >
                              {phase.title}
                            </Typography>

                            <Box
                              sx={{
                                backgroundColor: "#f8fdfe",
                                padding: "8px",
                                borderRadius: "8px",
                                border: "1px solid #5bbfd6",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  borderBottom: "1px solid #3095ab",
                                  paddingBottom: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    minWidth: "90px",
                                    paddingX: "12px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    Duration
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    backgroundColor: "#fcfeff",
                                    padding: "8px 12px",
                                    borderRadius: "4px",
                                    border: "1px solid #99d4e1",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {phase.duration}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ paddingLeft: "8px" }}>
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    color: "#065b70",
                                    lineHeight: 1.4,
                                    fontFamily: "Rethink Sans, sans-serif",
                                  }}
                                >
                                  <strong>Key milestones:</strong>{" "}
                                  {phase.milestones}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                {/* Requirements Completed and Deadlines */}
                <StyledAccordion
                  expanded={expanded === "complianceComparison"}
                  onChange={handleAccordionChange("complianceComparison")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <RequirementsIcon />
                      <AccordionTitle>
                        What are the requirements completed, and deadlines?
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      // border: "2px solid #35d7e4",
                      // borderRadius: "4px",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {/* Facility revalidation dates from backend (avoids "last revalidation date not on record") */}
                      {(complianceData.facilityContext?.last_medicare_revalidation_date ||
                        complianceData.facilityContext?.last_medicaid_revalidation_date) && (
                        <Box
                          sx={{
                            backgroundColor: "#e8f7fa",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid #9EE5F4",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#065b70",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            {complianceData.facilityContext.last_medicare_revalidation_date && (
                              <>Last Medicare revalidation: {complianceData.facilityContext.last_medicare_revalidation_date}</>
                            )}
                            {complianceData.facilityContext.last_medicare_revalidation_date &&
                              complianceData.facilityContext.last_medicaid_revalidation_date && " · "}
                            {complianceData.facilityContext.last_medicaid_revalidation_date && (
                              <>Last Medicaid revalidation: {complianceData.facilityContext.last_medicaid_revalidation_date}</>
                            )}
                          </Typography>
                        </Box>
                      )}
                      {/* Completed Section */}
                      {complianceData.sections.complianceComparison
                        ?.completed &&
                        complianceData.sections.complianceComparison.completed
                          .length > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #cff7ff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Box sx={{ width: "20px", height: "20px" }}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M6 3C4.34315 3 3 4.34315 3 6V14C3 15.6569 4.34315 17 6 17H14C15.6569 17 17 15.6569 17 14V6C17 4.34315 15.6569 3 14 3H6ZM4 6C4 4.89543 4.89543 4 6 4H14C15.1046 4 16 4.89543 16 6V14C16 15.1046 15.1046 16 14 16H6C4.89543 16 4 15.1046 4 14V6ZM13.3536 8.35355C13.5488 8.15829 13.5488 7.84171 13.3536 7.64645C13.1583 7.45118 12.8417 7.45118 12.6464 7.64645L9 11.2929L7.35355 9.64645C7.15829 9.45118 6.84171 9.45118 6.64645 9.64645C6.45118 9.84171 6.45118 10.1583 6.64645 10.3536L8.64645 12.3536C8.84171 12.5488 9.15829 12.5488 9.35355 12.3536L13.3536 8.35355Z"
                                    fill="#065B70"
                                  />
                                </svg>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Completed
                              </Typography>
                            </Box>
                            {complianceData.sections.complianceComparison.completed.map(
                              (item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    backgroundColor: "#f8fdfe",
                                    padding: "8px",
                                    borderRadius: "8px",
                                    border: "1px solid #5bbfd6",
                                    marginBottom:
                                      index <
                                      complianceData.sections
                                        .complianceComparison.completed.length -
                                        1
                                        ? "8px"
                                        : "0",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      lineHeight: 1.4,
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {typeof item === "string"
                                      ? item
                                      : getPendingItemDisplayText(
                                          item,
                                          complianceData.facilityContext
                                        )}
                                  </Typography>
                                  {typeof item === "object" &&
                                    item.citation?.title && (
                                      <Box
                                        sx={{
                                          backgroundColor: "white",
                                          padding: "4px 16px",
                                          borderRadius: "16px",
                                          border: "1px solid #ccf2f9",
                                          display: "inline-block",
                                          marginTop: "8px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#065b70",
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                          }}
                                        >
                                          {item.citation?.url ? (
                                            <a
                                              href={item.citation.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "#0ea5e9",
                                                textDecoration: "none",
                                              }}
                                            >
                                              📎 {item.citation.title}
                                            </a>
                                          ) : (
                                            <>Source: {item.citation.title}</>
                                          )}
                                        </Typography>
                                      </Box>
                                    )}
                                </Box>
                              )
                            )}
                          </Box>
                        )}

                      {/* Pending Section */}
                      {complianceData.sections.complianceComparison?.pending &&
                        complianceData.sections.complianceComparison.pending
                          .length > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #cff7ff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Box sx={{ width: "20px", height: "20px" }}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M13.69 14.6C13.8891 15.0402 13.9947 15.5169 14 16H6C6.00608 15.5159 6.11516 15.0387 6.32 14.6C6.52759 14.1497 6.83544 13.753 7.22 13.44L9.04 11.92C9.32073 11.6855 9.54611 11.3918 9.7 11.06C9.85933 10.7294 9.9414 10.367 9.94 10H10.06C10.0741 10.3709 10.1733 10.7336 10.35 11.06C10.5014 11.3878 10.7234 11.678 11 11.91L12.78 13.44C13.1652 13.7548 13.476 14.1509 13.69 14.6ZM5.19531 17.8438C5.42448 17.9479 5.66667 18 5.92188 18H14.0781C14.3385 17.9948 14.5833 17.9427 14.8125 17.8438C15.0417 17.7448 15.2448 17.6042 15.4219 17.4219C15.599 17.2396 15.7396 17.0339 15.8438 16.8047C15.9479 16.5755 16 16.3333 16 16.0781C16 15.6667 15.9557 15.2682 15.8672 14.8828C15.7786 14.4974 15.6536 14.125 15.4922 13.7656C15.3307 13.4062 15.1302 13.0703 14.8906 12.7578C14.651 12.4453 14.375 12.1589 14.0625 11.8984L12.2422 10.3828C12.1849 10.3359 12.1406 10.2786 12.1094 10.2109C12.0781 10.1432 12.0625 10.0729 12.0625 10C12.0625 9.84896 12.1224 9.72135 12.2422 9.61719L14.0625 8.09375C14.6979 7.5625 15.1797 6.9401 15.5078 6.22656C15.8359 5.51302 16 4.74479 16 3.92188C15.9948 3.66146 15.9427 3.41667 15.8438 3.1875C15.7448 2.95833 15.6042 2.75521 15.4219 2.57812C15.2396 2.40104 15.0339 2.26042 14.8047 2.15625C14.5755 2.05208 14.3333 2 14.0781 2H5.92188C5.66146 2.00521 5.41667 2.05729 5.1875 2.15625C4.95833 2.25521 4.75521 2.39583 4.57812 2.57812C4.40104 2.76042 4.26042 2.96615 4.15625 3.19531C4.05208 3.42448 4 3.66667 4 3.92188C4.00521 4.74479 4.17188 5.51302 4.5 6.22656C4.82812 6.9401 5.30729 7.5625 5.9375 8.09375L7.75781 9.61719C7.8151 9.66406 7.85938 9.72135 7.89062 9.78906C7.92188 9.85677 7.9375 9.92708 7.9375 10C7.9375 10.151 7.8776 10.2786 7.75781 10.3828L5.9375 11.8984C5.625 12.1641 5.34896 12.4531 5.10938 12.7656C4.86979 13.0781 4.66667 13.4141 4.5 13.7734C4.33333 14.1328 4.20833 14.5052 4.125 14.8906C4.04167 15.276 4 15.6719 4 16.0781C4.00521 16.3385 4.05729 16.5833 4.15625 16.8125C4.25521 17.0417 4.39583 17.2448 4.57812 17.4219C4.76042 17.599 4.96615 17.7396 5.19531 17.8438ZM14.4062 16.9297C14.2917 16.9766 14.1693 17 14.0391 17H5.96094C5.82031 16.9948 5.69531 16.9688 5.58594 16.9219C5.47656 16.875 5.375 16.8073 5.28125 16.7188C5.1875 16.6302 5.11719 16.5286 5.07031 16.4141C5.02344 16.2995 5 16.1745 5 16.0391C5.00521 15.3828 5.14323 14.763 5.41406 14.1797C5.6849 13.5964 6.07292 13.0938 6.57812 12.6719L8.39844 11.1484C8.57031 11.0026 8.70312 10.8307 8.79688 10.6328C8.89062 10.4349 8.9375 10.224 8.9375 10C8.9375 9.77604 8.89062 9.5625 8.79688 9.35938C8.70312 9.15625 8.57031 8.98438 8.39844 8.84375L6.57812 7.32812C6.31771 7.11458 6.09375 6.88281 5.90625 6.63281C5.71875 6.38281 5.55469 6.11198 5.41406 5.82031C5.27344 5.52865 5.16927 5.22656 5.10156 4.91406C5.03385 4.60156 5 4.28385 5 3.96094C5.00521 3.82031 5.03125 3.69531 5.07812 3.58594C5.125 3.47656 5.19271 3.375 5.28125 3.28125C5.36979 3.1875 5.47135 3.11719 5.58594 3.07031C5.70052 3.02344 5.82552 3 5.96094 3H14.0391C14.1797 3.01042 14.3047 3.03646 14.4141 3.07812C14.5234 3.11979 14.625 3.1875 14.7188 3.28125C14.8125 3.375 14.8828 3.47917 14.9297 3.59375C14.9766 3.70833 15 3.83073 15 3.96094C14.9948 4.6276 14.8568 5.25 14.5859 5.82812C14.3151 6.40625 13.9271 6.90625 13.4219 7.32812L11.6016 8.84375C11.4297 8.98438 11.2969 9.15625 11.2031 9.35938C11.1094 9.5625 11.0625 9.77604 11.0625 10C11.0625 10.224 11.1094 10.4349 11.2031 10.6328C11.2969 10.8307 11.4297 11.0026 11.6016 11.1484L13.4219 12.6719C13.9323 13.099 14.3229 13.6016 14.5938 14.1797C14.8646 14.7578 15 15.3776 15 16.0391C14.9896 16.1797 14.9635 16.3047 14.9219 16.4141C14.8802 16.5234 14.8125 16.625 14.7188 16.7188C14.625 16.8125 14.5208 16.8828 14.4062 16.9297Z"
                                    fill="#065B70"
                                  />
                                </svg>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Pending
                              </Typography>
                            </Box>
                            {complianceData.sections.complianceComparison.pending.map(
                              (item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    backgroundColor: "#f8fdfe",
                                    padding: "8px",
                                    borderRadius: "8px",
                                    border: "1px solid #5bbfd6",
                                    marginBottom:
                                      index <
                                      complianceData.sections
                                        .complianceComparison.pending.length -
                                        1
                                        ? "8px"
                                        : "0",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      lineHeight: 1.4,
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {typeof item === "string"
                                      ? item
                                      : getPendingItemDisplayText(
                                          item,
                                          complianceData.facilityContext
                                        )}
                                  </Typography>
                                  {typeof item === "object" &&
                                    item.citation?.title && (
                                      <Box
                                        sx={{
                                          backgroundColor: "white",
                                          padding: "4px 16px",
                                          borderRadius: "16px",
                                          border: "1px solid #ccf2f9",
                                          display: "inline-block",
                                          marginTop: "8px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#065b70",
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                          }}
                                        >
                                          {item.citation?.url ? (
                                            <a
                                              href={item.citation.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "#0ea5e9",
                                                textDecoration: "none",
                                              }}
                                            >
                                              📎 {item.citation.title}
                                            </a>
                                          ) : (
                                            <>Source: {item.citation.title}</>
                                          )}
                                        </Typography>
                                      </Box>
                                    )}
                                </Box>
                              )
                            )}
                          </Box>
                        )}

                      {/* Updates Section */}
                      {complianceData.sections.complianceComparison?.updates &&
                        complianceData.sections.complianceComparison.updates
                          .length > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #cff7ff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Box sx={{ width: "20px", height: "20px" }}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M5 6.5C5 6.22386 5.22386 6 5.5 6H12.5C12.7761 6 13 6.22386 13 6.5C13 6.77614 12.7761 7 12.5 7H5.5C5.22386 7 5 6.77614 5 6.5ZM10.5 9C10.2239 9 10 9.22386 10 9.5C10 9.77614 10.2239 10 10.5 10H12.5C12.7761 10 13 9.77614 13 9.5C13 9.22386 12.7761 9 12.5 9H10.5ZM10 12.5C10 12.2239 10.2239 12 10.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H10.5C10.2239 13 10 12.7761 10 12.5ZM5.5 9C5.22386 9 5 9.22386 5 9.5V12.5C5 12.7761 5.22386 13 5.5 13H8.5C8.77614 13 9 12.7761 9 12.5V9.5C9 9.22386 8.77614 9 8.5 9H5.5ZM6 12V10H8V12H6ZM2 5C2 3.89543 2.89543 3 4 3H14C15.1046 3 16 3.89543 16 5V6C17.1046 6 18 6.89543 18 8V13.5C18 14.8807 16.8807 16 15.5 16H4.5C3.11929 16 2 14.8807 2 13.5V5ZM15 5C15 4.44772 14.5523 4 14 4L4 4C3.44771 4 3 4.44772 3 5L3 13.5C3 14.3284 3.67157 15 4.5 15H15.5C16.3284 15 17 14.3284 17 13.5V8C17 7.44772 16.5523 7 16 7V13.5C16 13.7761 15.7761 14 15.5 14C15.2239 14 15 13.7761 15 13.5V5Z"
                                    fill="#065B70"
                                  />
                                </svg>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Updates
                              </Typography>
                            </Box>
                            {complianceData.sections.complianceComparison.updates.map(
                              (item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    backgroundColor: "#f8fdfe",
                                    padding: "8px",
                                    borderRadius: "8px",
                                    border: "1px solid #5bbfd6",
                                    marginBottom:
                                      index <
                                      complianceData.sections
                                        .complianceComparison.updates.length -
                                        1
                                        ? "8px"
                                        : "0",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#065b70",
                                      lineHeight: 1.4,
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {typeof item === "string"
                                      ? item
                                      : getPendingItemDisplayText(
                                          item,
                                          complianceData.facilityContext
                                        )}
                                  </Typography>
                                  {typeof item === "object" &&
                                    item.citation?.title && (
                                      <Box
                                        sx={{
                                          backgroundColor: "white",
                                          padding: "4px 16px",
                                          borderRadius: "16px",
                                          border: "1px solid #ccf2f9",
                                          display: "inline-block",
                                          marginTop: "8px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#065b70",
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                          }}
                                        >
                                          {item.citation?.url ? (
                                            <a
                                              href={item.citation.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "#0ea5e9",
                                                textDecoration: "none",
                                              }}
                                            >
                                              📎 {item.citation.title}
                                            </a>
                                          ) : (
                                            <>Source: {item.citation.title}</>
                                          )}
                                        </Typography>
                                      </Box>
                                    )}
                                </Box>
                              )
                            )}
                          </Box>
                        )}

                      {/* Deadlines Section */}
                      {complianceData.sections.complianceComparison
                        ?.deadlines &&
                        complianceData.sections.complianceComparison.deadlines
                          .length > 0 && (
                          <Box
                            sx={{
                              backgroundColor: "#f8fdfe",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #cff7ff",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <Box sx={{ width: "20px", height: "20px" }}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M14.5 3C15.8807 3 17 4.11929 17 5.5V9.59971C16.6832 9.43777 16.3486 9.30564 16 9.20703V7H4V14.5C4 15.3284 4.67157 16 5.5 16H9.20703C9.30564 16.3486 9.43777 16.6832 9.59971 17H5.5C4.11929 17 3 15.8807 3 14.5V5.5C3 4.11929 4.11929 3 5.5 3H14.5ZM14.5 4H5.5C4.67157 4 4 4.67157 4 5.5V6H16V5.5C16 4.67157 15.3284 4 14.5 4ZM19 14.5C19 16.9853 16.9853 19 14.5 19C12.0147 19 10 16.9853 10 14.5C10 12.0147 12.0147 10 14.5 10C16.9853 10 19 12.0147 19 14.5ZM14.5 15.2071L15.6464 16.3536C15.8417 16.5488 16.1583 16.5488 16.3536 16.3536C16.5488 16.1583 16.5488 15.8417 16.3536 15.6464L15.2071 14.5L16.3536 13.3536C16.5488 13.1583 16.5488 12.8417 16.3536 12.6464C16.1583 12.4512 15.8417 12.4512 15.6464 12.6464L14.5 13.7929L13.3536 12.6464C13.1583 12.4512 12.8417 12.4512 12.6464 12.6464C12.4512 12.8417 12.4512 13.1583 12.6464 13.3536L13.7929 14.5L12.6464 15.6464C12.4512 15.8417 12.4512 16.1583 12.6464 16.3536C12.8417 16.5488 13.1583 16.5488 13.3536 16.3536L14.5 15.2071Z"
                                    fill="#065B70"
                                  />
                                </svg>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Deadlines
                              </Typography>
                            </Box>
                            {complianceData.sections.complianceComparison.deadlines.map(
                              (item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    backgroundColor: "#f8fdfe",
                                    padding: "8px",
                                    borderRadius: "8px",
                                    border: "1px solid #5bbfd6",
                                    marginBottom:
                                      index <
                                      complianceData.sections
                                        .complianceComparison.deadlines.length -
                                        1
                                        ? "8px"
                                        : "0",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      borderBottom: "1px solid #3095ab",
                                      paddingBottom: "8px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "#065b70",
                                        fontFamily: "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {item.name}
                                    </Typography>
                                  </Box>
                                  {item.rule && (
                                    <Box
                                      sx={{
                                        paddingLeft: "8px",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          color: "#065b70",
                                          lineHeight: 1.4,
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        {item.rule}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: "16px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {item.nextDueDate && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            paddingLeft: "8px",
                                            width: "128px",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#065b70",
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                            }}
                                          >
                                            Due:
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            backgroundColor: "#fcfeff",
                                            padding: "8px 12px",
                                            borderRadius: "4px",
                                            border: "1px solid #99d4e1",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#065b70",
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                            }}
                                          >
                                            {formatDate(item.nextDueDate)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                    {item.frequency && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            paddingLeft: "8px",
                                            width: "128px",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#065b70",
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                            }}
                                          >
                                            Frequency:
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            backgroundColor: "#fcfeff",
                                            padding: "8px 12px",
                                            borderRadius: "4px",
                                            border: "1px solid #99d4e1",
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "14px",
                                              fontWeight: 600,
                                              color: "#065b70",
                                              fontFamily:
                                                "Rethink Sans, sans-serif",
                                            }}
                                          >
                                            {item.frequency}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                  {typeof item === "object" &&
                                    item.citation?.title && (
                                      <Box
                                        sx={{
                                          backgroundColor: "white",
                                          padding: "4px 16px",
                                          borderRadius: "16px",
                                          border: "1px solid #ccf2f9",
                                          display: "inline-block",
                                          marginTop: "8px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            color: "#065b70",
                                            fontFamily:
                                              "Rethink Sans, sans-serif",
                                          }}
                                        >
                                          {item.citation?.url ? (
                                            <a
                                              href={item.citation.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                color: "#0ea5e9",
                                                textDecoration: "none",
                                              }}
                                            >
                                              📎 {item.citation.title}
                                            </a>
                                          ) : (
                                            <>Source: {item.citation.title}</>
                                          )}
                                        </Typography>
                                      </Box>
                                    )}
                                </Box>
                              )
                            )}
                          </Box>
                        )}

                      {/* Fallback message when no compliance comparison data */}
                      {!(
                        (complianceData?.sections?.complianceComparison?.completed?.length ?? 0) > 0 ||
                        (complianceData?.sections?.complianceComparison?.pending?.length ?? 0) > 0 ||
                        (complianceData?.sections?.complianceComparison?.updates?.length ?? 0) > 0 ||
                        (complianceData?.sections?.complianceComparison?.deadlines?.length ?? 0) > 0
                      ) && (
                        <Box
                          sx={{
                            backgroundColor: "#f8fdfe",
                            padding: "16px",
                            borderRadius: "8px",
                            border: "1px solid #cff7ff",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#6b7280",
                              fontStyle: "italic",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            No compliance comparison data available. This information will be populated when you provide facility-specific compliance documentation or when the system completes its regulatory analysis.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>

                {/* Audit Ready Checklist */}
                {complianceData.sections.auditReadyChecklist &&
                  complianceData.sections.auditReadyChecklist.length > 0 && (
                <StyledAccordion
                  expanded={expanded === "auditReadyChecklist"}
                  onChange={handleAccordionChange("auditReadyChecklist")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckListIcon />
                      <AccordionTitle>
                        Give me an audit ready checklist
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      // border: "2px solid #35d7e4",
                      // borderRadius: "4px",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {complianceData.sections.auditReadyChecklist &&
                        complianceData.sections.auditReadyChecklist.length >
                          0 && (
                          <AuditChecklistTable
                            items={
                              complianceData.sections.auditReadyChecklist
                            }
                          />
                        )}
                    </Box>
                  </AccordionDetails>
                </StyledAccordion>
                )}

                {/* Compliance Calendar with Penalty Exposure - closed by default */}
                {facilityComplianceCalendarAccordions.map((cfg) => (
                  <StyledAccordion
                    key={cfg.accordionKey}
                    expanded={expanded === cfg.accordionKey}
                    onChange={handleAccordionChange(cfg.accordionKey)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        borderRadius: "4px",
                        border: "1px solid #9EE5F4",
                        backgroundColor: "#FFF",
                        boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PenaltyIcon />
                        <AccordionTitle>
                        {cfg.title}
                        </AccordionTitle>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        backgroundColor: "#ffffff",
                        padding: "12px",
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "#f8fdfe",
                          padding: "8px",
                          borderRadius: "8px",
                          border: "1px solid #cff7ff",
                        }}
                      >
                        {/* Calendar Table */}
                        <Box
                          sx={{
                            border: "1px solid #0a829d",
                            borderRadius: "4px",
                            width: "100%",
                          }}
                        >
                          <Box sx={{ overflowX: "auto", width: "100%" }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 0,
                                width: "100%",
                              }}
                            >
                          {/* Table Header — outside vertical scroll so the scrollbar does not overlap the teal bar */}
                          <Box
                            sx={{
                              backgroundColor: "#0a829d",
                              pt: "12px",
                              pb: "12px",
                              pl: "16px",
                              pr: "26px",
                              display: "grid",
                              gridTemplateColumns: "10% 5% 12% 15% 10% 18% 1fr",
                              gap: "8px",
                              alignItems: "center",
                              borderTopLeftRadius: "4px",
                              borderTopRightRadius: "4px",
                              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                            }}
                          >
                            <Box sx={{ paddingX: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Deadline
                              </Typography>
                            </Box>
                            <Box
                              sx={{ textAlign: "center", paddingX: "8px" }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Status
                              </Typography>
                            </Box>
                            <Box sx={{ paddingX: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Items
                              </Typography>
                            </Box>
                            <Box sx={{ paddingX: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Requirement
                              </Typography>
                            </Box>
                            <Box sx={{ paddingX: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Source
                              </Typography>
                            </Box>
                            <Box sx={{ paddingX: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                  fontFamily: "Rethink Sans, sans-serif",
                                }}
                              >
                                Penalty if Missed
                              </Typography>
                            </Box>
                            <Box sx={{ paddingX: "8px" }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "white",
                                }}
                              >
                                Est. Cost Exposure
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              maxHeight: "min(65vh, 640px)",
                              minHeight: 0,
                              flex: "1 1 auto",
                              overflowY: "auto",
                              overflowX: "visible",
                              borderBottomLeftRadius: "4px",
                              borderBottomRightRadius: "4px",
                              scrollbarGutter: "stable",
                              "&::-webkit-scrollbar": {
                                width: "10px",
                                height: "10px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "rgba(10, 130, 157, 0.4)",
                                borderRadius: "5px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "rgba(0, 0, 0, 0.06)",
                              },
                            }}
                          >
                          {/* Table Rows */}
                          {cfg.items.map(
                            (calendarItem, index) => {
                              const isOngoing =
                                (calendarItem as { cadence?: string }).cadence ===
                                  "ongoing" ||
                                (calendarItem as { deadlineType?: string })
                                  .deadlineType === "none";
                              const deadline = isOngoing
                                ? null
                                : new Date(calendarItem.deadline);
                              const today = new Date();
                              const daysUntilDeadline =
                                deadline != null && !Number.isNaN(deadline.getTime())
                                  ? differenceInDays(
                                      startOfDay(deadline),
                                      startOfDay(today),
                                    )
                                  : 0;
                              const itemTitle = String(calendarItem.item ?? "");
                              const requirementText = String(
                                (calendarItem as { requirement?: string })
                                  .requirement ?? "",
                              );
                              const applicableSource = `${itemTitle} ${requirementText}`;
                              const ifApplicableRow =
                                /\*\*If applicable\*\*/i.test(applicableSource) ||
                                /(?:^|[\s(])If applicable\b/i.test(
                                  applicableSource,
                                );
                              const isRequired = ifApplicableRow
                                ? calendarItem.required === true
                                : calendarItem.required !== false;
                              const isOverdue =
                                !isOngoing &&
                                deadline != null &&
                                !Number.isNaN(deadline.getTime()) &&
                                daysUntilDeadline < 0;
                              const isHighRisk = isRequired && isOverdue;
                              const penaltyFormatted = formatCurrency(
                                calendarItem.penaltyIfMissed,
                              );
                              const penaltyTipEnabled =
                                Boolean(
                                  penaltyFormatted &&
                                    penaltyFormatted.trim().length > 0,
                                );

                              return (
                                <Box
                                  key={index}
                                  data-testid="compliance-calendar-row"
                                  sx={{
                                    borderTop:
                                      index > 0
                                        ? "1px solid #e5f5f8"
                                        : "1px solid transparent",
                                    padding: "16px 16px",
                                    display: "grid",
                                    gridTemplateColumns: "10% 5% 12% 15% 10% 18% 1fr",
                                    gap: "8px",
                                    alignItems: "start",
                                    minHeight: "64px",
                                  }}
                                >
                                  {/* Deadline column: date + days/overdue badge */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      gap: "4px",
                                      paddingX: "8px",
                                      paddingTop: "2px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "4px",
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                      >
                                        <path
                                          d="M5.5 17C4.11929 17 3 15.8807 3 14.5V10.5C3 10.2239 3.22386 10 3.5 10C3.77614 10 4 10.2239 4 10.5V14.5C4 15.3284 4.67157 16 5.5 16H14.5C15.3284 16 16 15.3284 16 14.5V6.99997L15.9941 7L6.4913 7C6.4913 6.64209 6.364 6.28417 6.10939 6H15.9941L16 6.00003V5.5C16 4.67157 15.3284 4 14.5 4H5.5C4.67157 4 4 4.67157 4 5.5V7.29289L4.64645 6.64645C4.84171 6.45118 5.15829 6.45118 5.35355 6.64645C5.54882 6.84171 5.54882 7.15829 5.35355 7.35355L3.85355 8.85355C3.65829 9.04882 3.34171 9.04882 3.14645 8.85355L1.64645 7.35355C1.45118 7.15829 1.45118 6.84171 1.64645 6.64645C1.84171 6.45118 2.15829 6.45118 2.35355 6.64645L3 7.29289V5.5C3 4.11929 4.11929 3 5.5 3H14.5C15.8807 3 17 4.11929 17 5.5V14.5C17 15.8807 15.8807 17 14.5 17H5.5ZM8 10C8 10.5523 7.55228 11 7 11C6.44772 11 6 10.5523 6 10C6 9.44771 6.44772 9 7 9C7.55228 9 8 9.44771 8 10ZM7 14C7.55228 14 8 13.5523 8 13C8 12.4477 7.55228 12 7 12C6.44772 12 6 12.4477 6 13C6 13.5523 6.44772 14 7 14ZM11 10C11 10.5523 10.5523 11 10 11C9.44771 11 9 10.5523 9 10C9 9.44771 9.44771 9 10 9C10.5523 9 11 9.44771 11 10ZM10 14C10.5523 14 11 13.5523 11 13C11 12.4477 10.5523 12 10 12C9.44771 12 9 12.4477 9 13C9 13.5523 9.44771 14 10 14ZM14 10C14 10.5523 13.5523 11 13 11C12.4477 11 12 10.5523 12 10C12 9.44771 12.4477 9 13 9C13.5523 9 14 9.44771 14 10Z"
                                          fill="#065B70"
                                        />
                                      </svg>
                                      <Typography
                                        data-testid="compliance-calendar-deadline"
                                        sx={{
                                          fontSize: "12px",
                                          fontWeight: 400,
                                          color: "#262626",
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {isOngoing
                                          ? "Ongoing"
                                          : formatDate(calendarItem.deadline)}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        backgroundColor: isOngoing
                                          ? "#f0f4c3"
                                          : isOverdue
                                          ? "#fce4ec"
                                          : "#99d4e1",
                                        borderRadius: "2px",
                                        padding: "1px 6px",
                                        textAlign: "center",
                                        alignSelf: "flex-start",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "11px",
                                          fontWeight: 500,
                                          color: isOngoing
                                            ? "#33691e"
                                            : isOverdue
                                            ? "#c62828"
                                            : "#065b70",
                                          fontFamily: "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        {isOngoing
                                          ? "Standing duty"
                                          : isOverdue
                                          ? "OVERDUE"
                                          : `${daysUntilDeadline} Days`}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Status column */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "flex-start",
                                      paddingX: "8px",
                                      paddingTop: "2px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        backgroundColor: isHighRisk
                                          ? "#fce4ec"
                                          : isRequired
                                          ? "#e0f4f8"
                                          : "#fff8e1",
                                        borderRadius: "2px",
                                        padding: "2px 8px",
                                        textAlign: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          fontWeight: 400,
                                          color: isHighRisk
                                            ? "#c62828"
                                            : isRequired
                                            ? "#065b70"
                                            : "#7a5800",
                                          fontFamily: "Rethink Sans, sans-serif",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "3px",
                                          justifyContent: "center",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {isHighRisk ? (
                                          <>
                                            <svg
                                              width="12"
                                              height="12"
                                              viewBox="0 0 15 15"
                                              fill="none"
                                            >
                                              <path
                                                d="M7.5 2L13.5 12.5H1.5L7.5 2Z"
                                                fill="#c62828"
                                                stroke="#c62828"
                                                strokeWidth="0.5"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M7.5 6V9"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                              />
                                              <circle
                                                cx="7.5"
                                                cy="10.5"
                                                r="0.75"
                                                fill="white"
                                              />
                                            </svg>
                                            High Risk
                                          </>
                                        ) : isRequired ? (
                                          "Required"
                                        ) : (
                                          "Recommended"
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ paddingX: "8px" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        color: "#262626",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {calendarItem.item}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ paddingX: "8px" }}>
                                    <a
                                      href={calendarItem.citation.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        fontSize: "12px",
                                        color: "#065B70",
                                        textDecoration: "underline",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {calendarItem.requirement}
                                    </a>
                                  </Box>

                                  <Box sx={{ paddingX: "8px" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        color: "#065b70",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      {calendarItem.citation?.publisher ||
                                        "Government Authority"}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ paddingX: "8px", minWidth: 0 }}>
                                    <Tooltip
                                      placement="top-start"
                                      enterDelay={350}
                                      disableHoverListener={!penaltyTipEnabled}
                                      title={
                                        penaltyTipEnabled ? (
                                          <CompliancePenaltyTooltipTitle
                                            text={penaltyFormatted}
                                          />
                                        ) : (
                                          ""
                                        )
                                      }
                                      slotProps={{
                                        tooltip: {
                                          sx: {
                                            bgcolor: "#fff5f3",
                                            color: "#7a1f24",
                                            maxWidth:
                                              "min(580px, calc(100vw - 32px))",
                                            p: 1.75,
                                            border: "1px solid #e8c4be",
                                            boxShadow:
                                              "0 8px 24px rgba(10, 40, 50, 0.12)",
                                          },
                                        },
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "12px",
                                          fontWeight: 400,
                                          color: "#a4262c",
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                          overflow: "hidden",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 3,
                                          WebkitBoxOrient: "vertical",
                                          wordBreak: "break-word",
                                          cursor: penaltyTipEnabled
                                            ? "help"
                                            : "default",
                                        }}
                                      >
                                        ⚠️ {penaltyFormatted}
                                      </Typography>
                                    </Tooltip>
                                  </Box>

                                  <Box sx={{ paddingX: "8px" }}>
                                    <Typography
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        color: "#262626",
                                        fontFamily:
                                          "Rethink Sans, sans-serif",
                                      }}
                                    >
                                      💰{" "}
                                      {formatCurrency(
                                        calendarItem.estimatedCostExposure,
                                        false
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            }
                          )}
                          </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </StyledAccordion>
                ))}

                {/* Potential Penalties & Compliance Risks */}
                <StyledAccordion
                  expanded={expanded === "penaltiesAndTotalExposure"}
                  onChange={handleAccordionChange("penaltiesAndTotalExposure")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PenaltyIcon />
                      <AccordionTitle>
                        Potential Penalties & Compliance Risks
                      </AccordionTitle>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "12px",
                      position: "relative",
                    }}
                  >
                    {/* ── Tab strip ── */}
                    <Box
                      sx={{
                        display: "flex",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {(
                        [
                          {
                            id: "penalties" as const,
                            label: "Potential Penalties",
                            count:
                              complianceData.sections.penaltiesAndTotalExposure
                                ?.items?.length ?? 0,
                          },
                          ...(fcRecurringCostsTabCount > 0
                            ? [
                                {
                                  id: "recurring" as const,
                                  label: "Recurring Costs",
                                  count: fcRecurringCostsTabCount,
                                },
                              ]
                            : []),
                          {
                            id: "contingent" as const,
                            label: "Contingent Risks",
                            count:
                              complianceData.sections.penaltiesAndTotalExposure
                                ?.rollup?.contingentRisks?.length ?? 0,
                          },
                        ] as const
                      ).map((tab) => {
                        const isActive = penaltySubTab === tab.id;
                        return (
                          <Box
                            key={tab.id}
                            onClick={() => setPenaltySubTab(tab.id)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "10px 16px",
                              cursor: "pointer",
                              borderBottom: isActive
                                ? "2px solid #065b70"
                                : "2px solid transparent",
                              marginBottom: "-1px",
                              color: isActive ? "#065b70" : "#6b7280",
                              fontFamily: "Rethink Sans, sans-serif",
                              fontSize: "14px",
                              fontWeight: isActive ? 600 : 400,
                              userSelect: "none",
                              transition: "color 0.15s",
                              "&:hover": {
                                color: "#065b70",
                                backgroundColor: "#f8fdfe",
                              },
                            }}
                          >
                            {tab.label}
                            {tab.count > 0 && (
                              <Box
                                sx={{
                                  minWidth: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  backgroundColor: isActive
                                    ? "#065b70"
                                    : "#e5e7eb",
                                  color: isActive ? "#fff" : "#6b7280",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  px: "4px",
                                }}
                              >
                                {tab.count}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>

                    {/* ── Potential Penalties table ── */}
                    {penaltySubTab === "penalties" && (
                      <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden", mt: "12px" }}>
                      <Box sx={{ overflowX: "auto" }}>
                        <Table
                          size="small"
                          sx={{ width: "100%", tableLayout: "auto" }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                VIOLATION
                              </TableCell>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                DESCRIPTION
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...fcPenaltyTableHeaderSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                AMOUNT
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...fcPenaltyTableHeaderSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                RECURRING
                              </TableCell>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                CALCULATION NOTES
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(
                              complianceData.sections.penaltiesAndTotalExposure
                                ?.items ?? []
                            ).map((item: any, idx: number) => {
                              const amount =
                                item?.workedExample?.result?.amount ?? 0;
                              const showAmount = Number(amount) > 0;
                              const recurring = fcDetectRecurring(item);
                              return (
                                <TableRow
                                  key={idx}
                                  sx={{
                                    "&:last-child td": {
                                      borderBottom: "none",
                                    },
                                    "&:hover": {
                                      backgroundColor: "#f8fdfe",
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      fontWeight: 600,
                                      minWidth: 140,
                                      color: "#065b70",
                                    }}
                                  >
                                    {item.name}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      minWidth: 200,
                                      color: "#374151",
                                    }}
                                  >
                                    {item.ruleOrQuote}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      color: "#065b70",
                                    }}
                                  >
                                    {showAmount
                                      ? formatCurrencyAmount(amount)
                                      : "—"}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {recurring ? (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          px: "10px",
                                          py: "3px",
                                          borderRadius: "12px",
                                          border: "1px solid #f59e0b",
                                          backgroundColor: "#fffbeb",
                                          color: "#92400e",
                                          fontSize: "12px",
                                          fontWeight: 500,
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        Yes
                                      </Box>
                                    ) : (
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          px: "10px",
                                          py: "3px",
                                          borderRadius: "12px",
                                          border: "1px solid #d1d5db",
                                          backgroundColor: "#f9fafb",
                                          color: "#6b7280",
                                          fontSize: "12px",
                                          fontWeight: 500,
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        No
                                      </Box>
                                    )}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      fontStyle: "italic",
                                      color: "#6b7280",
                                      minWidth: 160,
                                    }}
                                  >
                                    {item.workedExample?.calculationNotes
                                      ? stripMarkdownLight(
                                          item.workedExample.calculationNotes,
                                        )
                                      : "—"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        {(complianceData.sections.penaltiesAndTotalExposure
                          ?.items?.length ?? 0) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderTop: "2px solid #e5e7eb",
                              px: 2,
                              py: 1.5,
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#6b7280",
                                fontFamily: "Rethink Sans, sans-serif",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                              }}
                            >
                              SUBTOTAL — POTENTIAL PENALTIES
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#065b70",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {formatCurrencyAmount(
                                (
                                  complianceData.sections
                                    .penaltiesAndTotalExposure?.items ?? []
                                ).reduce(
                                  (sum: number, item: any) =>
                                    sum +
                                    (Number(
                                      item?.workedExample?.result?.amount,
                                    ) || 0),
                                  0,
                                ),
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      </Box>
                    )}

                    {/* ── Recurring Costs table ── */}
                    {penaltySubTab === "recurring" && (
                      <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden", mt: "12px" }}>
                      <Box sx={{ overflowX: "auto" }}>
                        <Table
                          size="small"
                          sx={{ width: "100%", tableLayout: "auto" }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                FEE
                              </TableCell>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                DESCRIPTION
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...fcPenaltyTableHeaderSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ANNUAL AMOUNT
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...fcPenaltyTableHeaderSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                FREQUENCY
                              </TableCell>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                AUTHORITY / REFERENCE
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(
                              complianceData.sections.penaltiesAndTotalExposure
                                ?.rollup?.recurringAnnualCosts ?? []
                            )
                              .filter((c: any) => c != null)
                              .map((cost: any, idx: number) => {
                                const hasValidAmount =
                                  cost?.amount?.amount !== null &&
                                  cost?.amount?.amount !== undefined;
                                const displayAmount = hasValidAmount
                                  ? formatCurrencyAmount(cost.amount.amount)
                                  : cost?.amount?.notes ?? "Fee varies";
                                return (
                                  <TableRow
                                    key={idx}
                                    sx={{
                                      "&:last-child td": {
                                        borderBottom: "none",
                                      },
                                      "&:hover": {
                                        backgroundColor: "#f8fdfe",
                                      },
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        ...fcPenaltyTableCellSx,
                                        fontWeight: 600,
                                        minWidth: 160,
                                        color: "#065b70",
                                      }}
                                    >
                                      {cost.label}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...fcPenaltyTableCellSx,
                                        minWidth: 180,
                                        color: "#374151",
                                      }}
                                    >
                                      {hasValidAmount && cost?.amount?.notes
                                        ? cost.amount.notes
                                        : cost?.description ?? "—"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...fcPenaltyTableCellSx,
                                        fontWeight: 700,
                                        whiteSpace: "nowrap",
                                        color: hasValidAmount
                                          ? "#065b70"
                                          : "#9a3412",
                                        fontStyle: hasValidAmount
                                          ? "normal"
                                          : "italic",
                                      }}
                                    >
                                      {displayAmount}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...fcPenaltyTableCellSx,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          px: "10px",
                                          py: "3px",
                                          borderRadius: "12px",
                                          border: "1px solid #d1d5db",
                                          backgroundColor: "#f9fafb",
                                          color: "#374151",
                                          fontSize: "12px",
                                          fontWeight: 500,
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                        }}
                                      >
                                        Annual
                                      </Box>
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        ...fcPenaltyTableCellSx,
                                        color: "#6b7280",
                                      }}
                                    >
                                      {(cost as any)?.citation?.publisher ??
                                        (cost as any)?.citation?.title ??
                                        (cost as any)?.reference ??
                                        "—"}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                        {(
                          complianceData.sections.penaltiesAndTotalExposure?.rollup?.recurringAnnualCosts?.filter(
                            (c: any) => c != null,
                          ).length ?? 0
                        ) > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderTop: "2px solid #e5e7eb",
                              px: 2,
                              py: 1.5,
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#6b7280",
                                fontFamily: "Rethink Sans, sans-serif",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                              }}
                            >
                              SUBTOTAL — RECURRING ANNUAL COSTS
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#065b70",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {formatCurrencyAmount(
                                (
                                  complianceData.sections
                                    .penaltiesAndTotalExposure?.rollup
                                    ?.recurringAnnualCosts ?? []
                                )
                                  .filter((c: any) => c != null)
                                  .reduce(
                                    (sum: number, cost: any) =>
                                      sum +
                                      (Number(cost?.amount?.amount) || 0),
                                    0,
                                  ),
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      </Box>
                    )}

                    {/* ── Contingent Risks table ── */}
                    {penaltySubTab === "contingent" && (
                      <Box sx={{ border: "1px solid #0a829d", borderRadius: "4px", overflow: "hidden", mt: "12px" }}>
                      <Box sx={{ overflowX: "auto" }}>
                        <Table
                          size="small"
                          sx={{ width: "100%", tableLayout: "auto" }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                RISK
                              </TableCell>
                              <TableCell sx={fcPenaltyTableHeaderSx}>
                                TRIGGER CONDITION
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...fcPenaltyTableHeaderSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                MAX EXPOSURE
                              </TableCell>
                              <TableCell
                                sx={{
                                  ...fcPenaltyTableHeaderSx,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                RISK LEVEL
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(
                              complianceData.sections.penaltiesAndTotalExposure
                                ?.rollup?.contingentRisks ?? []
                            ).map((risk: any, idx: number) => {
                              const info = fcExtractContingentRiskInfo(risk);
                              const riskColor =
                                info.riskLevel === "High"
                                  ? "#dc2626"
                                  : info.riskLevel === "Medium"
                                    ? "#d97706"
                                    : "#16a34a";
                              return (
                                <TableRow
                                  key={idx}
                                  sx={{
                                    "&:last-child td": {
                                      borderBottom: "none",
                                    },
                                    "&:hover": {
                                      backgroundColor: "#f8fdfe",
                                    },
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      fontWeight: 600,
                                      minWidth: 160,
                                      color: "#065b70",
                                    }}
                                  >
                                    {info.name}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      minWidth: 220,
                                      color: "#374151",
                                    }}
                                  >
                                    {info.trigger}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      fontWeight: 600,
                                      color: "#065b70",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {info.exposure}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...fcPenaltyTableCellSx,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {info.riskLevel && (
                                      <Box
                                        sx={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "6px",
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                          fontSize: "13px",
                                          fontWeight: 500,
                                          color: riskColor,
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: riskColor,
                                            flexShrink: 0,
                                          }}
                                        />
                                        {info.riskLevel}
                                      </Box>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                      </Box>
                    )}
                  </AccordionDetails>
                </StyledAccordion>

                {/* Applicable CFR Regulations Accordion */}
                <StyledAccordion
                  expanded={expanded === "applicableCfrRegulations"}
                  onChange={handleAccordionChange("applicableCfrRegulations")}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      borderRadius: "4px",
                      border: "1px solid #9EE5F4",
                      backgroundColor: "#FFF",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CfrRegulationsIcon />
                      <AccordionTitle>Applicable CFR Regulations</AccordionTitle>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      backgroundColor: "#ffffff",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {/* Sub-accordion: 45 CFR Part 160 */}
                    <StyledAccordion
                      expanded={expandedCfr === "cfr160"}
                      onChange={handleCfrAccordionChange("cfr160")}
                      sx={{ marginBottom: 0 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          borderRadius: "4px",
                          border: "1px solid #9EE5F4",
                          backgroundColor: "#F8FDFF",
                          boxShadow: "0 2px 6px 0 rgba(111, 211, 232, 0.12)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          45 CFR Part 160 — General Administrative Requirements (HIPAA)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 1, pb: 2, px: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              fontFamily: "Rethink Sans, sans-serif",
                              lineHeight: 1.6,
                            }}
                          >
                            Establishes the general administrative requirements that covered entities and business associates must follow as part of the Health Insurance Portability and Accountability Act (HIPAA). Enforced by the HHS Office for Civil Rights (OCR).
                          </Typography>

                          {[
                            {
                              subpart: "Subpart A — General Provisions",
                              items: [
                                "Definitions of covered entities, business associates, and protected health information (PHI)",
                                "Applicability to health plans, health care clearinghouses, and health care providers that transmit health information electronically",
                                "Applicability extended to business associates under the HITECH Act",
                              ],
                            },
                            {
                              subpart: "Subpart B — Preemption of State Law",
                              items: [
                                "HIPAA generally preempts contrary state laws unless the state law is more stringent",
                                "Exceptions granted by the Secretary of HHS where state laws address compelling public interest needs",
                                "Facilities must evaluate state-specific privacy laws to determine whether stricter standards apply",
                              ],
                            },
                            {
                              subpart: "Subpart C — Compliance and Investigations",
                              items: [
                                "OCR may investigate complaints and conduct compliance reviews",
                                "Covered entities must cooperate with investigations and provide requested documentation",
                                "Corrective action plans (CAPs) may be required following findings of non-compliance",
                              ],
                            },
                            {
                              subpart: "Subpart D — Imposition of Civil Money Penalties (CMPs)",
                              items: [
                                "Penalties tiered by culpability: $100–$50,000 per violation, up to $1.9M per violation category per calendar year",
                                "Factors considered include nature and extent of the violation, harm caused, and history of prior violations",
                                "Affirmative defenses and waivers available under specific circumstances",
                              ],
                            },
                            {
                              subpart: "Subpart E — Requirements for Maintaining Records",
                              items: [
                                "Covered entities must maintain HIPAA-required documentation for 6 years from creation or last effective date",
                                "Records must be available to HHS upon request during an investigation or compliance review",
                              ],
                            },
                          ].map(({ subpart, items }) => (
                            <Box key={subpart}>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                  mb: 0.5,
                                }}
                              >
                                {subpart}
                              </Typography>
                              <Box
                                component="ul"
                                sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}
                              >
                                {items.map((item) => (
                                  <Box
                                    key={item}
                                    component="li"
                                    sx={{
                                      fontSize: "13px",
                                      color: "#374151",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {item}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </StyledAccordion>

                    {/* Sub-accordion: 45 CFR Part 92 */}
                    <StyledAccordion
                      expanded={expandedCfr === "cfr92"}
                      onChange={handleCfrAccordionChange("cfr92")}
                      sx={{ marginBottom: 0 }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          borderRadius: "4px",
                          border: "1px solid #9EE5F4",
                          backgroundColor: "#F8FDFF",
                          boxShadow: "0 2px 6px 0 rgba(111, 211, 232, 0.12)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          45 CFR Part 92 — Nondiscrimination in Health Programs and Activities (Section 1557 / ACA)(HHS-OCR)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 1, pb: 2, px: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              fontFamily: "Rethink Sans, sans-serif",
                              lineHeight: 1.6,
                            }}
                          >
                            Implements Section 1557 of the Affordable Care Act (ACA), prohibiting discrimination on the basis of race, color, national origin, sex, age, or disability in health programs and activities receiving federal financial assistance from HHS. Enforced by the HHS Office for Civil Rights (OCR).
                          </Typography>

                          {[
                            {
                              subpart: "Covered Entities",
                              items: [
                                "Health programs and activities receiving federal financial assistance from HHS (e.g., Medicaid, Medicare Part A, ACA marketplace plans)",
                                "Health insurance issuers offering plans through ACA Marketplaces",
                                "HHS-administered health programs regardless of federal financial assistance",
                              ],
                            },
                            {
                              subpart: "Prohibited Discrimination",
                              items: [
                                "Discrimination based on race, color, national origin, sex (including pregnancy, sex stereotyping, and gender identity), age, or disability",
                                "Denial of benefits, services, or participation on any prohibited basis",
                                "Use of criteria or methods that have a discriminatory effect",
                              ],
                            },
                            {
                              subpart: "Language Access & Effective Communication",
                              items: [
                                "Covered entities must take reasonable steps to provide meaningful access to individuals with limited English proficiency (LEP), including qualified interpreters and translated materials",
                                "Auxiliary aids and services must be provided to individuals with disabilities at no cost",
                                "Taglines informing LEP individuals of language assistance must be included in significant communications",
                              ],
                            },
                            {
                              subpart: "Accessibility Requirements",
                              items: [
                                "Physical facilities and electronic information must be accessible to individuals with disabilities",
                                "Patient portals and health plan websites must meet applicable accessibility standards",
                                "Medical equipment and diagnostic services must be provided in an accessible manner",
                              ],
                            },
                            {
                              subpart: "Administrative Requirements",
                              items: [
                                "Designate a responsible employee (Section 1557 Coordinator) to coordinate compliance efforts",
                                "Adopt and implement grievance procedures for prompt and equitable resolution of complaints",
                                "Post a notice of nondiscrimination in significant communications and on website",
                                "Maintain records sufficient to demonstrate compliance and provide to HHS upon request",
                              ],
                            },
                          ].map(({ subpart, items }) => (
                            <Box key={subpart}>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#065b70",
                                  fontFamily: "Rethink Sans, sans-serif",
                                  mb: 0.5,
                                }}
                              >
                                {subpart}
                              </Typography>
                              <Box
                                component="ul"
                                sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.5 }}
                              >
                                {items.map((item) => (
                                  <Box
                                    key={item}
                                    component="li"
                                    sx={{
                                      fontSize: "13px",
                                      color: "#374151",
                                      fontFamily: "Rethink Sans, sans-serif",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {item}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </StyledAccordion>
                  </AccordionDetails>
                </StyledAccordion>
              </Box>
            </HeaderSection>
          </MainContent>

          {/* <FadeOverlay /> */}

          {/* <ActionButtons>
          <ActionButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
            >
              <path
                d="M10 10.5C10 9.11929 11.1193 8 12.5 8H19.5C20.8807 8 22 9.11929 22 10.5V23.5C22 23.6881 21.8945 23.8602 21.7269 23.9456C21.5593 24.0309 21.358 24.015 21.2059 23.9044L16 20.1182L10.7941 23.9044C10.642 24.015 10.4407 24.0309 10.2731 23.9456C10.1055 23.8602 10 23.6881 10 23.5V10.5ZM12.5 9C11.6716 9 11 9.67157 11 10.5V22.5181L15.7059 19.0956C15.8812 18.9681 16.1188 18.9681 16.2941 19.0956L21 22.5181V10.5C21 9.67157 20.3284 9 19.5 9H12.5Z"
                fill="#065B70"
              />
            </svg>
          </ActionButton>
          <ActionButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="14"
              viewBox="0 0 15 14"
              fill="none"
            >
              <path
                d="M10.3298 9.83772L14.8269 5.41512L14.8841 5.34968C15.0558 5.12058 15.0367 4.78916 14.8268 4.58283L10.3297 0.161651L10.268 0.108643C9.9078 -0.160361 9.37757 0.0980532 9.37757 0.577825V2.70757L9.15299 2.72268C5.59039 3.00547 3.50344 5.25989 3.00472 9.3501C2.94067 9.87538 3.54318 10.2042 3.93261 9.85643C5.36448 8.57792 6.84382 7.78398 8.37757 7.46573C8.6236 7.41468 8.87103 7.37587 9.11989 7.34926L9.37757 7.32611V9.42161L9.38294 9.50392C9.4429 9.95663 9.99197 10.1699 10.3298 9.83772ZM9.2263 3.72L10.3776 3.64256V1.61104L13.8238 4.99905L10.3776 8.38816V6.23223L9.02198 6.35403L9.01358 6.35493C7.31135 6.53692 5.70337 7.21889 4.18674 8.35551C4.48493 7.01744 4.99417 6.01008 5.6632 5.28852C6.49235 4.39426 7.65261 3.84605 9.2263 3.72ZM2.5 1C1.11929 1 0 2.11929 0 3.5V11.5C0 12.8807 1.11929 14 2.5 14H10.5C11.8807 14 13 12.8807 13 11.5V10.5C13 10.2239 12.7761 10 12.5 10C12.2239 10 12 10.2239 12 10.5V11.5C12 12.3284 11.3284 13 10.5 13H2.5C1.67157 13 1 12.3284 1 11.5V3.5C1 2.67157 1.67157 2 2.5 2H5.5C5.77614 2 6 1.77614 6 1.5C6 1.22386 5.77614 1 5.5 1H2.5Z"
                fill="#065B70"
              />
            </svg>
          </ActionButton>
          <ActionButton>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
            >
              <path
                d="M16 9C13.7939 9 12 10.7939 12 13C12 13.2761 12.2239 13.5 12.5 13.5C12.7761 13.5 13 13.2761 13 13C13 11.3461 14.3461 10 16 10C17.6539 10 19 11.3461 19 13C19 14.2486 18.3078 14.8633 17.4246 15.6204L17.3935 15.647C16.5328 16.3845 15.5 17.2695 15.5 19V19.5C15.5 19.7761 15.7239 20 16 20C16.2761 20 16.5 19.7761 16.5 19.5V19C16.5 17.7514 17.1922 17.1367 18.0754 16.3796L18.1065 16.353C18.9672 15.6155 20 14.7305 20 13C20 10.7939 18.2061 9 16 9ZM16 23C16.4142 23 16.75 22.6642 16.75 22.25C16.75 21.8358 16.4142 21.5 16 21.5C15.5858 21.5 15.25 21.8358 15.25 22.25C15.25 22.6642 15.5858 23 16 23Z"
                fill="#065B70"
              />
            </svg>
          </ActionButton>
        </ActionButtons> */}
        </ContentWrapper>
      </ContentContainer>
    </MainContainer>
  );
};

export default FacilityCompliance;
